/**
 * The dashboard's strings (design 11 §4): ICU messages, one catalogue per locale. English first;
 * another locale is a file with the same ids. Every string a user reads comes from here.
 */
export const en = {
  "app.name": "Swaralo",
  "app.skip": "Skip to content",
  "app.loading": "Loading…",
  "app.signingIn": "Taking you to sign in…",

  "nav.label": "Sections",
  "nav.home": "Home",
  "nav.chat": "Chat",
  "nav.agents": "Agents",
  "nav.scripts": "Call Scripts",
  "nav.knowledge": "Knowledge",
  "nav.clips": "Clips",
  "nav.models": "Models",
  "nav.calls": "Calls",
  "nav.handoffs": "Handoffs",
  "nav.assist": "Agent assist",
  "nav.campaigns": "Campaigns",
  "nav.numbers": "Numbers",
  "nav.trunks": "Trunks",
  "nav.sipUsers": "SIP users",
  "nav.operators": "Operators",
  "nav.compliance": "Compliance",
  "nav.analytics": "Analytics",
  "nav.developers": "Developers",
  "nav.settings": "Settings",

  "header.search": "Search",
  "header.help": "Help",
  "header.tenant": "Tenant: {name}",
  "header.tenants": "Your tenants",
  "header.noTenant": "No tenant",
  "user.menu": "Account: {email}",
  "user.role": "Role: {role}",
  "user.theme": "Theme",
  "user.theme.system": "System",
  "user.theme.light": "Light",
  "user.theme.dark": "Dark",
  "user.signOut": "Sign out",

  "palette.label": "Search and commands",
  "palette.placeholder": "Go to a section or run a command…",
  "palette.empty": "Nothing matches.",
  "palette.sections": "Go to",
  "palette.commands": "Commands",
  "palette.themeLight": "Switch to the light theme",
  "palette.themeDark": "Switch to the dark theme",

  "role.owner": "Owner",
  "role.admin": "Admin",
  "role.developer": "Developer",
  "role.operator": "Operator",
  "role.viewer": "Viewer",
  "role.billing": "Billing",

  "state.error.title": "Something went wrong",
  "state.error.requestId": "Request id: {id}",
  "state.error.docs": "What this error means",
  "state.error.retry": "Try again",
  "state.denied.title": "You can't see this yet",
  "state.denied.body": "Your role in {tenant} is {role}. Ask an owner or admin of the tenant for a role that has access.",
  "state.soon.title": "Coming in {release}",
  "state.soon.body": "This section is planned for Swaralo {release}.",
  "state.noTenant.title": "You're not in a tenant yet",
  "state.noTenant.body": "Ask an owner of your organisation's tenant to invite {email}. You'll see it here once they have.",
  "state.notFound.title": "There's no page here",
  "state.notFound.home": "Go home",

  "home.title": "Welcome to Swaralo",
  "home.intro": "Set up a voice agent in a few steps. Each one links to where you do it.",
  "home.steps": "Getting started",
  "home.step.key": "Add a provider key",
  "home.step.agent": "Create an agent",
  "home.step.test": "Talk to it in your browser",
  "home.step.compliance": "Check your compliance profile",
  "home.step.complianceIs": "Your compliance profile: {profile}",
  "home.step.trunk": "Connect a phone line",
  "home.step.number": "Give it a phone number",
  "home.step.call": "Make a call",
  "home.step.done": "Done",
  "home.step.doneAt": "Done {when}",
  "home.step.todo": "To do",
  "home.hint.key": "One Sarvam or OpenAI key is enough to start.",
  "home.hint.agent": "From a template, or describe what it should do.",
  "home.hint.test": "Open your agent and start a browser test: no phone line needed.",
  "home.hint.compliance": "The rules your calls follow, from your country.",
  "home.hint.trunk": "A SIP trunk from your carrier.",
  "home.hint.number": "So callers can reach the agent.",
  "home.hint.call": "Call the number, or have the agent call you.",

  "agents.description": "Voice agents and their published versions.",
  "agents.empty": "No agents yet",
  "agents.emptyBody": "Start from a template: a receptionist, customer support, a reminder, and more.",
  "agents.emptyReadOnly": "Nobody in this tenant has created an agent yet.",
  "agents.breadcrumb": "Breadcrumb",
  "agents.versions": "Versions",
  "agents.col.version": "Version",
  "agents.col.status": "Status",
  "agents.col.created": "Created",
  "agents.status.draft": "Draft",
  "agents.status.published": "Published",
  "agents.status.archived": "Archived",
  "agents.direction.inbound": "Answers calls",
  "agents.direction.outbound": "Makes calls",
  "agents.direction.both": "Answers and makes calls",
  "agents.new.button": "New agent",
  "agents.new.title": "New agent",
  "line.title": "Connect a phone line",
  "line.body": "Swaralo doesn't sell phone numbers: calls come through a line you have, or get from a provider. Testing an agent in your browser needs no line at all.",
  "line.provider": "A provider account: Twilio or Exotel",
  "line.providerBody": "Your numbers there reach your agents through Swaralo's bridge. The quickest start if you have an account.",
  "line.sip": "Your own SIP trunk",
  "line.sipBody": "From your carrier, by IP or by registration: the most control, and the usual choice for a contact centre.",
  "line.whatsapp": "WhatsApp calls",
  "line.whatsappBody": "WhatsApp Business Calling, through Meta's SIP: customers call your business number in WhatsApp.",
  "line.open": "Set it up",
  "line.guide": "Guide: {title}",
  "line.later": "Do it later",
  "line.connect": "Connect a phone line",
  "agents.new.defaults": "Your default models",
  "agents.new.defaultsHint": "What new agents start with: {providers}. Set in the builder or on the Models page.",
  "defaults.title": "Default for new agents",
  "defaults.same": "These are your default models for new agents.",
  "defaults.differs": "These differ from your default models for new agents.",
  "defaults.use": "Use the default",
  "defaults.make": "Make these the default for new agents",
  "defaults.made": "New agents now start with these models.",
  "defaults.none": "No default yet: new agents start with their template's models. In an agent's builder, make its models the default.",
  "defaults.summary": "{role}: {provider}{model}",
  "defaults.clear": "Clear the default",
  "defaults.role.stt": "Speech to text",
  "defaults.role.llm": "Language model",
  "defaults.role.tts": "Voice",
  "agents.new.intro": "Describe it and the copilot drafts it, or start from a template. You can change everything in the builder.",
  "agents.new.template": "Template",
  "agents.new.name": "Name",
  "agents.new.nameHint": "Lowercase letters, digits and hyphens. It names the agent in the API and the CLI.",
  "agents.new.nameRule": "Use lowercase letters, digits and hyphens, starting and ending with a letter or digit.",
  "agents.new.nameTaken": "There is already an agent named {name}. Choose another name.",
  "agents.new.create": "Create agent",
  "agents.new.creating": "Creating…",
  "agents.new.providers": "Speech and model",
  "agents.new.copilotOffer": "Describe your agent in words and let the copilot draft it, with your {provider} key.",
  "agents.new.copilotUse": "Use {name} for the copilot",
  "agents.new.asTemplate": "As the template: {providers}",
  "agents.new.asTemplateHint": "Needs a key for each: {credentials}.",
  "agents.new.kit": "{label}, one key",
  "agents.new.kitSuggested": "You have this key",
  "agents.col.name": "Name",
  "agents.col.folder": "Folder",
  "agents.folder.filter": "Folder",
  "agents.folder.all": "All agents",
  "agents.folder.none": "No folder",
  "agents.folder.option": "{name} ({count})",
  "agents.folder.empty": "No agents here.",
  "agents.folder.move": "Folder of {name}",
  "agents.folder.new": "New folder…",
  "agents.folder.newPrompt": "The new folder's name",
  "agents.folder.invalid": "A folder's name is 1-64 characters, without /, and does not start or end with a space.",
  "agents.export": "Download v{version} as YAML",
  "agents.import.button": "Import",
  "agents.import.title": "Import agents",
  "agents.import.intro": "YAML documents, as Download gives them or `swaralo apply` takes them. Check what they would do, then import. An agent that exists by name gets a new version; nothing is published.",
  "agents.import.file": "A YAML file",
  "agents.import.text": "Or the documents",
  "agents.import.check": "Check",
  "agents.import.apply": "Import ({changed})",
  "agents.import.preview": "What importing does: {changed} to create or change",
  "agents.import.previewTable": "What each document would do",
  "agents.import.kind": "Kind",
  "agents.import.name": "Name",
  "agents.import.action": "Does",
  "agents.import.action.create": "Create",
  "agents.import.action.update": "A new version",
  "agents.import.action.unchanged": "Nothing: it is the same as v{version}",
  "agents.import.changes": "Changes",
  "agents.col.published": "Published version",
  "agents.col.updated": "Updated",
  "agents.unpublished": "Not published",

  "agents.tabs": "Agent",
  "agents.tab.build": "Build",
  "agents.tab.versions": "Versions",
  "agents.tab.evals": "Evals",
  "agents.tab.copilot": "Copilot",
  "agents.tab.experiments": "Experiments",
  "experiments.title": "Experiments",
  "experiments.intro": "Compare versions of this agent on real calls. Calls that would take the published version are split between them; each caller always gets the same version. A version you pin on a number or a campaign is never split.",
  "experiments.none": "No experiment is running",
  "experiments.noneBody": "Start one to see which version does better: goal reached, QA score, handle time, transfers, latency and cost.",
  "experiments.oneVersion": "Save a second version first: an experiment compares at least two.",
  "experiments.new": "Start an experiment",
  "experiments.newIntro": "The first version is the control; the others are compared with it. Weights are each version's share of the calls.",
  "experiments.control": "Control",
  "experiments.variant": "Variant {n}",
  "experiments.weight": "Weight",
  "experiments.addVariant": "Add a version",
  "experiments.removeVariant": "Remove variant {n}",
  "experiments.goal": "Goal tag (optional)",
  "experiments.goalHint": "A post-call tag: the share of calls reaching it is compared.",
  "experiments.distinct": "Each version can be in the experiment once.",
  "experiments.start": "Start",
  "experiments.running": "Running: {versions}, since {since}",
  "experiments.goalIs": "Goal: {tag}",
  "experiments.calls": "v{version}: {calls} calls (weight {weight})",
  "experiments.callsControl": "v{version} (control): {calls} calls (weight {weight})",
  "experiments.noCalls": "No calls yet. Results appear as calls end and are reviewed.",
  "experiments.results": "Results by version",
  "experiments.metric": "Metric",
  "experiments.metric.goal": "Goal reached",
  "experiments.metric.qa_score": "QA score",
  "experiments.metric.handle_s": "Handle time",
  "experiments.metric.transferred": "Transferred",
  "experiments.metric.latency_ms": "Turn latency",
  "experiments.metric.cost": "Cost per call",
  "experiments.controlCol": "v{version} (control)",
  "experiments.tooFew": "Too few calls to compare yet",
  "experiments.significant": "{diff}: significant (p {p})",
  "experiments.notYet": "{diff}: not significant yet (p {p})",
  "experiments.howToRead": "Each version is compared with the control by a sequential test, so you can check as often as you like. A difference marked significant is unlikely to be chance. The number of calls measured is in brackets.",
  "experiments.promote": "Promote v{version}",
  "experiments.promoteConfirm": "Publish v{version} to all calls and end the experiment?",
  "experiments.stop": "Stop without changes",
  "experiments.stopConfirm": "Stop the experiment? The published version takes every call again.",
  "experiments.past": "Past experiments",
  "experiments.versions": "Versions",
  "experiments.started": "Started",
  "experiments.ended": "Ended",
  "experiments.outcome": "Outcome",
  "experiments.promoted": "v{version} promoted",
  "experiments.stopped": "Stopped",
  "agents.new.how": "How to start",
  "agents.new.fromTemplate": "From a template",
  "agents.new.describe": "Describe it",
  "evals.trigger.copilot": "Copilot",
  "copilot.intro": "The copilot proposes changes to this agent: from what you ask, from the ways its calls fail, or from one call. Each proposal is run against the agent's eval suites before you see it. Nothing changes until you accept it, and accepting makes a draft: you publish it as any other version.",
  "copilot.ask": "What should change?",
  "copilot.askPlaceholder": "e.g. Ask for the account number before reading the balance.",
  "copilot.askHint": "In your words: the copilot proposes the smallest change to the latest version.",
  "copilot.propose": "Propose",
  "copilot.proposing": "Proposing…",
  "copilot.proposeFix": "Propose a fix",
  "copilot.proposed": "Proposed: see it under Proposals.",
  "copilot.noModel": "The copilot has no model yet.",
  "copilot.noModelLink": "Choose one in Settings.",
  "copilot.proposals": "Proposals",
  "copilot.noProposals": "No proposals yet.",
  "copilot.untitled": "A proposal",
  "copilot.source.request": "Asked for",
  "copilot.source.cluster": "From a review: {title}",
  "copilot.source.call": "From call {call}",
  "copilot.onVersion": "changes v{version}",
  "copilot.status.proposed": "Proposed",
  "copilot.status.dismissed": "Dismissed",
  "copilot.accepted": "Accepted as v{version}",
  "copilot.acceptedNote": "v{version} is the agent's new draft.",
  "copilot.openBuilder": "Open it in Build",
  "copilot.accept": "Accept",
  "copilot.dismiss": "Dismiss",
  "copilot.stale": "The agent has a newer version than v{version}, which this proposal changes: ask again.",
  "copilot.diffLabel": "Changes: {summary}",
  "copilot.eval.none": "No eval suite ran on it.",
  "copilot.eval.title": "The agent's eval suites",
  "copilot.eval.evaluating": "Running the agent's eval suites on it…",
  "copilot.eval.suite": "Suite",
  "copilot.eval.before": "v{version}",
  "copilot.eval.after": "With the proposal",
  "copilot.eval.running": "Running…",
  "copilot.eval.error": "Could not run",
  "copilot.eval.passed": "{passed} of {total} passed",
  "copilot.reviews": "Failing calls",
  "copilot.reviewIntro": "A review takes the agent's calls that failed, scored low in QA, failed compliance QA or left the caller unhappy, and groups the ones that fail the same way.",
  "copilot.period": "Calls from",
  "copilot.lastDays": "{days, plural, one {the last day} other {the last # days}}",
  "copilot.review": "Review failing calls",
  "copilot.noReviews": "No review yet.",
  "copilot.reviewing": "Reviewing the calls…",
  "copilot.reviewFailed": "The review failed: {error}",
  "copilot.reviewDone": "{failing} of {seen} calls failing, {from} to {to}",
  "copilot.method.semantic": "grouped by meaning",
  "copilot.method.lexical": "grouped by the words they share",
  "copilot.noFailures": "No failing calls in this period.",
  "copilot.unnamed": "A group of failing calls",
  "copilot.clusterLabel": "{title}: {size, plural, one {# call} other {# calls}}",
  "copilot.calls": "{count, plural, one {# call} other {# calls}}",
  "copilot.likelyCause": "Likely cause:",
  "copilot.examples": "{count, plural, one {# example} other {# examples}}",
  "describe.direction": "Calls",
  "describe.inbound": "People call it",
  "describe.outbound": "It calls people",
  "describe.useCase": "What it's for",
  "describe.useCasePlaceholder": "e.g. Appointment booking, EMI reminders, order status",
  "describe.language": "Language it speaks",
  "describe.languageHint": "Guessed from what you write. Its speech is chosen for this language from the keys you have.",
  "describe.lang.en-IN": "English (India)",
  "describe.lang.en-US": "English (US)",
  "describe.lang.en-GB": "English (UK)",
  "describe.lang.hi-IN": "Hindi",
  "describe.lang.bn-IN": "Bengali",
  "describe.lang.ta-IN": "Tamil",
  "describe.lang.te-IN": "Telugu",
  "describe.lang.kn-IN": "Kannada",
  "describe.lang.ml-IN": "Malayalam",
  "describe.lang.mr-IN": "Marathi",
  "describe.lang.gu-IN": "Gujarati",
  "describe.lang.pa-IN": "Punjabi",
  "describe.lang.od-IN": "Odia",
  "describe.lang.es-ES": "Spanish",
  "describe.lang.fr-FR": "French",
  "describe.lang.de-DE": "German",
  "describe.more": "More: speech and language providers",
  "describe.baseAuto": "Chosen for its language",
  "describe.draftingFor": "Drafting… {seconds} s",
  "describe.cancel": "Cancel",
  "describe.slow": "A slow or local model can take a minute.",
  "describe.kitUsed": "Speech from your {kit} key, for its language.",
  "describe.what": "What should the agent do?",
  "describe.placeholder": "e.g. Book appointments for a dental clinic: ask for the patient's name, then agree a day and time, and confirm it.",
  "describe.hint": "The copilot writes its instructions, greeting, tools, steps and what to keep after each call. You review it before it is created.",
  "describe.base": "Speech and language providers from",
  "describe.baseHint": "The copilot never chooses a provider or a credential: the agent uses these.",
  "describe.templates": "Templates",
  "describe.agents": "Your agents",
  "describe.draft": "Draft it",
  "describe.drafting": "Drafting…",
  "describe.again": "Draft it again",
  "describe.preview": "The draft",
  "describe.greeting": "It says first",
  "describe.instructions": "Instructions",
  "describe.tools": "Tools",
  "describe.steps": "Steps",
  "describe.afterCall": "After each call",
  "describe.summary": "a summary",
  "describe.extraction": "fields extracted",
  "describe.qa": "{count, plural, one {# QA question} other {# QA questions}}",
  "describe.none": "none",
  "describe.warnings": "To fix before publishing",
  "describe.error.unavailable": "The copilot's model is unavailable right now.",
  "describe.error.timeout": "The draft took too long.",
  "describe.error.invalid": "The model's answers didn't make a valid agent.",
  "describe.tryAgain": "Try again",
  "describe.fallbackUsed": "Drafted by your fallback model: the copilot's own model was unavailable.",
  "describe.reviewNote": "A draft is a start: read it, then change it in the builder before you publish it.",
  "explain.title": "Copilot",
  "explain.ask": "Why did this call go wrong?",
  "explain.asking": "Reading the call…",
  "explain.cause": "Why:",
  "explain.evidence": "The turns that show it",
  "explain.turn": "Turn {turn}:",
  "explain.change": "What to change:",
  "explain.propose": "Propose this fix",
  "explain.proposed": "Proposed.",
  "explain.seeProposal": "See it in the agent's Copilot tab",
  "explain.needsWrite": "Asking the copilot needs permission to change agents.",
  "settings.copilot": "Copilot",
  "settings.copilotIntro": "The model the copilot asks to draft agents, review calls and propose fixes. It is billed to your account with that provider.",
  "settings.copilotModel": "Model",
  "settings.copilotProvider": "Provider",
  "settings.copilotCredential": "Credential",
  "settings.copilotEndpoint": "Model endpoint",
  "settings.copilotModelName": "Model name",
  "settings.copilotModelHint": "A capable model writes better agents, e.g. gpt-4.1. Leave it empty for the provider's default.",
  "settings.choose": "Choose…",
  "settings.addOne": "Add one",
  "settings.noCredentials": "No keys yet.",
  "settings.noEndpoints": "No model endpoints yet.",
  "settings.copilotFallback": "A fallback model for drafts",
  "settings.copilotFallbackHint": "Asked when the copilot's model is unavailable (an outage, a rate limit), so a draft still comes back. A local model is a good fallback.",
  "settings.copilotEmbedder": "Group failing calls by meaning, with an embedding model",
  "settings.copilotEmbedderHint": "Without one, a review groups calls by the words they share.",
  "settings.copilotEmbedderModelHint": "1024 dimensions, e.g. text-embedding-3-small.",
  "settings.copilotSave": "Save",
  "settings.copilotRemove": "Remove the model",
  "evals.description": "Suites of scenarios a version must pass: a simulated caller talks to the agent through a real worker (text), and each scenario's criteria are judged. Run one against a version here or from CI (swaralo eval run).",
  "evals.suites": "Suites",
  "evals.new": "New suite",
  "evals.noSuites": "No eval suites yet.",
  "evals.suiteFacts": "{scenarios, plural, one {# scenario} other {# scenarios}} · runs on {runsOn}",
  "evals.version": "Version",
  "evals.versionN": "v{version}",
  "evals.versionPublished": "v{version} (published)",
  "evals.run": "Run",
  "evals.runOne": "Run {name}",
  "evals.edit": "Edit",
  "evals.editOne": "Edit {name}",
  "evals.remove": "Delete {name}",
  "evals.removeConfirm": "Delete the eval suite {name} and its runs?",
  "evals.yaml": "Suite (YAML)",
  "evals.yamlHint": "kind: EvalSuite: scenarios with a script or a persona and goal, tool mocks, and their success criteria.",
  "evals.yamlBroken": "Not valid YAML: {why}",
  "evals.save": "Save suite",
  "evals.runs": "Runs",
  "evals.noRuns": "No runs yet.",
  "evals.suite": "Suite",
  "evals.trigger": "Trigger",
  "evals.trigger.manual": "By hand",
  "evals.trigger.ci": "CI",
  "evals.trigger.publish": "Publish",
  "evals.trigger.nightly": "Nightly",
  "evals.result": "Result",
  "evals.started": "Started",
  "evals.details": "Details",
  "evals.show": "Show",
  "evals.showRun": "Show the run of {suite} on v{version}",
  "evals.passedOf": "{passed} of {scenarios} passed",
  "evals.status.queued": "Queued",
  "evals.status.running": "Running",
  "evals.status.passed": "Passed",
  "evals.status.failed": "Failed",
  "evals.status.error": "Could not run",
  "evals.runTitle": "{suite} on v{version}",
  "evals.shootOut": "Models side by side",
  "evals.model": "Model",
  "evals.agentsOwn": "The agent's own",
  "evals.passed": "Passed",
  "evals.costPerConversation": "Cost per conversation",
  "evals.unpriced": "No price on file",
  "evals.pass": "Pass",
  "evals.fail": "Fail",
  "evals.verdicts": "Criteria of {scenario}",
  "evals.conversation": "The conversation ({turns, plural, one {# agent turn} other {# agent turns}})",
  "evals.role.agent": "Agent:",
  "evals.role.caller": "Caller:",
  "evals.role.tool": "Tool:",
  "evals.role.end": "Ended:",

  "versions.live": "v{version} is published: it takes the calls.",
  "versions.noneLive": "No version is published yet: this agent takes no calls.",
  "versions.by": "By",
  "versions.actions": "Actions",
  "versions.publish": "Publish…",
  "versions.publishOne": "Publish v{version}…",
  "versions.rollback": "Roll back to v{version}",
  "versions.compare": "Compare versions",
  "versions.from": "From",
  "versions.to": "To",
  "diff.summary": "{added, plural, one {# line added} other {# lines added}}, {removed, plural, one {# removed} other {# removed}}",
  "diff.none": "The two versions are the same.",
  "diff.lines": "From line {line}",
  "diff.added": "added",
  "diff.removed": "removed",
  "publish.button": "Publish v{version}…",
  "publish.saveFirst": "Save your changes first: publishing takes a saved version.",
  "publish.title": "Publish v{version}?",
  "publish.intro": "The published version takes every call to its numbers. Check what changes first.",
  "publish.firstTitle": "The first version to publish",
  "publish.first": "Nothing is published yet: from now on, this version takes the calls.",
  "publish.changes": "What changes against v{live}, published now",
  "publish.diffLabel": "Changes from v{from} to v{to}",
  "publish.numbers": "Numbers that switch to it",
  "publish.noNumbers": "No number routes to this agent yet.",
  "publish.pinned": "Pinned to a version, so they stay as they are: {list}",
  "publish.evaluation": "Evaluation",
  "publish.noSuites": "No eval suite runs on publish: add one in the Evals tab.",
  "publish.evalsPassed": "Every suite that runs on publish has passed against this version.",
  "publish.evalsWarn": "This version has not passed every suite that runs on publish. You may still publish it.",
  "publish.evalsBlock": "This version has not passed every suite that runs on publish, and the tenant requires it to. Run them in the Evals tab.",
  "publish.evalStatus.none": "not run on this version",
  "publish.evalStatus.queued": "queued",
  "publish.evalStatus.running": "running",
  "publish.evalStatus.passed": "passed",
  "publish.evalStatus.failed": "failed",
  "publish.evalStatus.error": "could not run",
  "publish.confirm": "Publish v{version}",
  "publish.publishing": "Publishing…",
  "test.title": "Test",
  "test.values": "Test values",
  "test.valuesHint": "What a call or campaign contact would give. Leave one empty for its default.",
  "test.firstTitle": "Talk to your agent",
  "test.firstBody": "Choose Browser call and start it, then speak: your microphone is the caller. No phone line is needed. Or type to it in Text chat.",
  "test.firstDismiss": "Got it",
  "test.how": "How to test",
  "test.browserCall": "Browser call",
  "test.textChat": "Text chat",
  "call.empty": "Call the agent from this browser, with your microphone. No phone line is needed.",
  "call.starting": "Starting the call…",
  "call.talk": "Say something: the agent is listening.",
  "call.start": "Call v{version}",
  "call.again": "Call v{version} again",
  "call.hangUp": "Hang up",
  "call.mute": "Mute",
  "call.unmute": "Unmute",
  "call.noMic": "The browser was not allowed to use the microphone. Allow it for this site, then try again.",
  "call.audioFailed": "The browser's audio could not start.",
  "test.needsWrite": "Testing an agent needs a role that can edit it.",
  "test.summary": "How the test went",
  "test.summaryTurns": "{count, plural, one {# turn} other {# turns}}",
  "test.summarySteps": "steps: {steps}",
  "test.summaryReply": "replies in {ms} ms on average",
  "test.summaryNext": "Next, make it better: the copilot can explain what happened and suggest changes, or edit it yourself.",
  "test.improve": "Improve it with the copilot",
  "test.editAgent": "Edit the agent",
  "created.title": "{name} is ready",
  "created.body": "It's a draft: nothing calls it yet, and you can change everything.",
  "created.greeting": "It says first: “{greeting}”",
  "created.steps": "Its steps: {steps}",
  "created.tools": "It can: {tools}",
  "created.next": "Next, talk to it in your browser. No phone line is needed.",
  "created.test": "Open and test",
  "created.later": "Later",
  "test.versionNote": "Tests v{version}, as saved. No phone call, and it is not in the call history.",
  "test.unsavedNote": "Tests your unsaved changes (from v{version}). Nothing is saved by testing.",
  "test.unsavedBroken": "Fix the YAML to test your changes: until then this tests v{version}.",
  "test.conversation": "The test conversation",
  "test.empty": "Start a chat to talk to the agent in text.",
  "test.starting": "Starting the chat…",
  "test.you": "You",
  "test.agent": "Agent",
  "test.latency": "{ms} ms",
  "test.nowAt": "Now at: {node}",
  "test.stages": "Where the time went",
  "test.stage.stt": "speech to text {ms} ms",
  "test.stage.llm_ttft": "model {ms} ms",
  "test.stage.tool": "tools {ms} ms",
  "test.stage.tts_ttfb": "voice {ms} ms",
  "test.rerun": "Run again",
  "test.rerunTurn": "Run turn {turn} again",
  "test.edit": "Edit",
  "test.editTurn": "Edit turn {turn}",
  "test.rewind": "Rewind here",
  "test.rewindTurn": "Rewind to before turn {turn}",
  "test.editing": "Editing turn {turn}: what you send replaces it and everything after it.",
  "test.cancelEdit": "Cancel",
  "test.rewound": "Back to before turn {turn}.",
  "test.rewindRefused.replying": "Wait for the agent's reply, then try again.",
  "test.rewindRefused.no_such_turn": "That turn is no longer in the conversation.",
  "test.toolRunning": "running…",
  "test.toolNoResult": "no result: the test ended first",
  "test.toolArgs": "Arguments",
  "test.toolCall": "Tool call: {name}",
  "test.speaking": "speaking…",
  "call.live": "Live",
  "call.messages": "{count, plural, one {# message} other {# messages}}",
  "test.toolOk": "done in {ms} ms",
  "test.toolFailed": "did not happen ({error})",
  "test.message": "Message",
  "test.placeholder": "Type what a caller would say…",
  "test.send": "Send",
  "test.end": "End",
  "test.start": "Chat with v{version}",
  "test.again": "Chat again with v{version}",
  "test.atNode": "Now at {node}",
  "test.endedByAgent": "The chat has ended.",
  "test.busy": "No agent worker is free right now: try again in a moment.",
  "test.failed": "The agent could not run: check its providers and credentials.",
  "test.refused": "The chat was refused: sign in again, or start a new one.",
  "test.closed": "The chat closed ({reason}).",
  "builder.check": "Check",
  "builder.checking": "Checking…",
  "builder.allGood": "No problems found.",
  "builder.issueCount": "{errors, plural, =0 {No errors} one {# error} other {# errors}}, {warnings, plural, =0 {no warnings} one {# warning} other {# warnings}}",
  "builder.show": "Show",
  "builder.view": "View",
  "builder.view.form": "Form",
  "builder.view.yaml": "YAML",
  "builder.view.flow": "Flow",
  "builder.undo": "Undo",
  "builder.redo": "Redo",
  "flow.canvas": "Flow canvas",
  "flow.followTest": "Follow the test: keep the step it is at in view",
  "flow.tools": "Flow tools",
  "flow.none": "This agent has no flow: it follows its instructions freely. Add a node to start one: the conversation then moves from node to node, each with its own task and functions.",
  "flow.realtime": "A realtime agent has no flow: flows run on cascaded agents (STT, LLM and TTS).",
  "flow.start": "Start",
  "flow.issues": "{count, plural, one {# issue} other {# issues}}",
  "flow.nodeLabel": "Node {name}{start, select, true {, the start} other {}}{count, plural, =0 {} one {, # issue} other {, # issues}}{test, select, here {, the test is here} visited {, visited by the test} other {}}",
  "flow.edgeLabel": "Move {name} from {from} to {to}{taken, select, true {, taken} other {}}",
  "flow.here": "Test",
  "flow.fromAnywhere": "From anywhere",
  "flow.anywhere": "anywhere",
  "flow.outline": "Outline",
  "flow.outlineStart": "{name} (start)",
  "flow.movesFrom": "Moves from {from}",
  "flow.inspector": "Inspector",
  "flow.startFlow": "Start a flow",
  "flow.starter.greet": "Greet the caller and ask how you can help.",
  "flow.starter.help": "Help the caller with what they asked.",
  "flow.starter.goodbye": "Thank the caller and say goodbye.",
  "flow.starter.toHelp": "The caller has said what they need",
  "flow.starter.toGoodbye": "The caller has nothing more to ask",
  "flow.anywhereHint": "Functions offered at every node: a move from anywhere, or a tool the agent can always use. Choose a node or a move to edit it.",
  "flow.moveFrom": "A move from {from}",
  "flow.nodeHeading": "Node {name}",
  "flow.nodeName": "Node name",
  "flow.task": "Task",
  "flow.taskN": "Task {n}",
  "flow.taskHint": "What the agent does in this step: the model reads it when the conversation enters the node.",
  "flow.addTask": "Add a task message",
  "flow.removeTask": "Remove task message {n}",
  "flow.roleMessage": "Role message",
  "flow.roleMessageHint": "Optional: who the agent is from this node on, in place of its instructions.",
  "flow.respondAtOnce": "Respond at once",
  "flow.respondAtOnceHint": "On: the agent speaks as soon as the node starts. Off: it waits for the caller.",
  "flow.context": "Conversation so far",
  "flow.context.default": "Keep it (the default)",
  "flow.context.append": "Keep it",
  "flow.context.reset": "Start afresh: only this node's task",
  "flow.functions": "Functions",
  "flow.offerTool": "Offer a tool",
  "flow.noTools": "The agent has no tools",
  "flow.offer": "Offer",
  "flow.moveLegend": "Move {name}",
  "flow.toolLegend": "Tool {name}",
  "flow.goesTo": "Goes to",
  "flow.tool": "Tool",
  "flow.then": "Then",
  "flow.then.stay": "Stay at this node",
  "flow.then.node": "Go to a node",
  "flow.then.branch": "Choose by its result",
  "flow.resultField": "The result's field",
  "flow.resultFieldHint": "A field of what the tool returns, e.g. status: its value chooses the case.",
  "flow.case": "Case {n}",
  "flow.caseGoesTo": "Case {n} goes to",
  "flow.removeCase": "Remove case {n}",
  "flow.addCase": "Add a case",
  "flow.otherwise": "Otherwise",
  "flow.remove": "Remove {name}",
  "flow.before": "Before",
  "flow.after": "After",
  "flow.addBefore": "Add an action before",
  "flow.addAfter": "Add an action after",
  "flow.removeAction": "Remove {label}",
  "flow.action.say": "Say something",
  "flow.action.end": "End the call",
  "flow.said": "What is said",
  "flow.sayText": "One moment, please.",
  "flow.addNode": "Add a node",
  "flow.addMove": "Add a move",
  "flow.addMoveFrom": "Add a move from {from}",
  "flow.delete": "Delete",
  "flow.makeStart": "Make it the start",
  "flow.layOut": "Lay out again",
  "flow.name": "Name",
  "flow.nameHint": "Letters, digits, _ and -: the conversation's events name the node by it.",
  "flow.newTask": "Describe what the agent does in this step.",
  "flow.from": "From",
  "flow.to": "To",
  "flow.functionName": "Function name",
  "flow.functionNameHint": "The model calls it to move: letters, digits, _ and -.",
  "flow.when": "When to take it",
  "flow.whenHint": "The model reads this to decide when to move, e.g. \"The caller wants to buy\".",
  "flow.add": "Add",
  "flow.cancel": "Cancel",
  "builder.yamlLabel": "The YAML of {name}",
  "builder.yamlBroken": "{count, plural, one {The YAML has a problem} other {The YAML has # problems}}, marked in the YAML view. The form is read-only until it is fixed.",
  "builder.more": "More settings",
  "builder.foldVariables": "{count, plural, =0 {Variables} other {Variables (#)}}",
  "builder.foldGreeting": "A recording instead, and interruptions",
  "builder.foldSpeech": "More speech settings: the voice's id and speed, key terms, fallbacks",
  "builder.foldBrain": "More model settings: temperature, reply length, fallbacks",
  "builder.moreSet": "{section} ({count})",
  "builder.moreNone": "all as they come",
  "builder.save": "Save",
  "builder.saved": "Saved v{version}",
  "builder.saving": "Saving…",
  "builder.discard": "Discard changes",
  "builder.unsaved": "Unsaved changes",
  "builder.conflict": "Someone saved a newer version of this agent while you were editing. Copy your changes from the YAML view, reload the page, and apply them again.",
  "builder.invalid": "The agent could not be saved:",
  "builder.leaveTitle": "Leave without saving?",
  "builder.leaveBody": "Your changes to this agent are not saved. If you leave, they are lost.",
  "builder.stay": "Stay",
  "builder.leave": "Leave",
  "builder.default": "Default",
  "builder.defaultModel": "Default ({model})",

  "builder.mode": "Mode",
  "builder.mode.cascaded": "Cascaded",
  "builder.mode.cascadedHint": "Speech to text, a language model, then text to speech. Pick each one.",
  "builder.mode.realtime": "Realtime",
  "builder.mode.realtimeHint": "One speech-to-speech model hears and speaks.",
  "builder.persona": "Persona and instructions",
  "builder.description": "Description",
  "builder.language": "Language",
  "builder.languageHint": "A language tag, like en-US or hi-IN.",
  "builder.instructions": "Instructions",
  "builder.instructionsHint": "What the agent is and how it behaves. Use '{{vars.name}}' for a variable and '{{call.from}}' for the caller''s number.",
  "builder.usedTitle": "Used in this agent",
  "builder.used.colName": "Variable",
  "builder.used.colWhere": "Where",
  "builder.used.colValue": "On a call",
  "builder.used.call": "From the call",
  "builder.used.handoff": "From a handoff",
  "builder.used.undeclared": "Not declared: said as nothing",
  "builder.used.default": "Default: {value}",
  "builder.used.noDefault": "Declared, no default: give it with each call",
  "builder.used.declare": "Declare it",
  "builder.variables": "Variables",
  "builder.variablesHint": "Values given for each call, used in the instructions and greeting.",
  "builder.varType": "Type",
  "builder.varDefault": "Default",
  "builder.varRequired": "Required",
  "builder.type.string": "Text",
  "builder.type.number": "Number",
  "builder.type.boolean": "Yes or no",
  "builder.newVariable": "New variable",
  "builder.addVariable": "Add",
  "builder.removeVariable": "Remove the variable {name}",
  "builder.greeting": "Greeting",
  "builder.greetingText": "First thing the agent says",
  "builder.greetingHint": "Leave it empty for the agent to wait for the caller.",
  "builder.greetingInterruptible": "The caller can interrupt the greeting",
  "builder.speech": "Speech",
  "builder.stt": "Speech to text",
  "builder.tts": "Text to speech",
  "builder.voice": "Voice",
  "builder.voiceHint": "The provider's voice id: chosen from the list above, or typed.",
  "voices.loading": "Loading the voices…",
  "voices.noList": "{provider} has no voice list here: type the voice id.",
  "voices.needCredential": "Choose a credential to see the voices of {provider}.",
  "voices.failed": "The voices could not be listed: {detail}",
  "voices.find": "Find a voice",
  "voices.list": "Voices of {provider}",
  "voices.none": "No voice matches.",
  "voices.hear": "Hear it",
  "voices.speaking": "Getting the sample…",
  "voices.stop": "Stop",
  "voices.tooMany": "That is enough previews for a minute: a provider charges for each one.",
  "builder.voiceSpeed": "Speed",
  "builder.brain": "Brain",
  "builder.realtime": "Speech-to-speech model",
  "builder.provider": "Provider",
  "builder.model": "Model",
  "builder.credential": "Credential",
  "builder.noCredential": "None chosen",
  "builder.credentialHint": "No {provider} credential yet: add one before publishing.",
  "builder.testing": "{name} (testing)",
  "builder.temperature": "Temperature",
  "builder.temperatureHint": "Lower is more predictable; higher is more varied.",
  "builder.maxTokens": "Longest reply (tokens)",
  "builder.noFallbacks": "No fallback providers. Add them in the YAML view.",
  "builder.endpoint": "Model endpoint",
  "builder.endpointHint": "One of your own models, registered under Models.",
  "builder.noEndpoints": "No {kind} model endpoint yet: register one under Models.",
  "builder.routing": "{role} routing",
  "builder.routeStrategy": "When a provider cannot be used",
  "builder.route.list": "Try the fallbacks in order (a fallback list)",
  "builder.route.fallback": "Try the targets in order (a route)",
  "builder.route.residency": "Keep the data where the compliance profile allows",
  "builder.route.fallbackHint": "The next provider takes over when the one before fails.",
  "builder.route.residencyHint": "When a call starts, the targets the tenant's profile refuses are dropped; the first left is used.",
  "builder.route.latency": "Lead with the fastest",
  "builder.route.latencyHint": "Each call leads with the target whose first byte came soonest in the last 15 minutes; one call in ten tries a target not yet measured. A failing target goes last.",
  "builder.route.cost": "Lead with the cheapest",
  "builder.route.costHint": "Each call leads with the target whose typical minute costs least; an unpriced or failing target goes last.",
  "builder.route.weighted": "Split by weight (A/B)",
  "builder.route.weightedHint": "Each call's leader is drawn by these weights; the others are its fallbacks.",
  "builder.route.weight": "Weight of {name}",
  "builder.routeTargets": "Fallbacks and targets (edit in YAML): {list}",
  "builder.firstByte": "Switch after (seconds without an answer)",
  "builder.firstByteHint": "Default: {seconds} s.",
  "builder.cost": "Estimated cost",
  "builder.costPerMinute": "About {amount} a minute",
  "builder.costUnknown": "The estimate is not available.",
  "builder.costUnpriced": "Not counted, no price on file: {list}",
  "builder.costAssumptions": "A minute is assumed to be {stt} s of listening, {input} tokens in and {output} out, and {characters} characters spoken. List prices, before tax: an estimate, not a quote.",
  "builder.conversation": "Conversation",
  "builder.turnDetection": "End of turn",
  "builder.turn.smart": "Smart turn (a model)",
  "builder.turn.timeout": "After a silence",
  "builder.turn.stt": "The speech-to-text provider's",
  "builder.interruptions": "The caller can interrupt the agent",
  "builder.minWords": "Words before it counts as an interruption",
  "builder.minWordsHint": "Short sounds like “uh-huh” are ignored.",
  "builder.idleTimeout": "Seconds of silence before a prompt",
  "builder.idleHangup": "Prompts before hanging up",
  "builder.maxDuration": "Longest call (seconds)",
  "builder.knowledge": "Knowledge",
  "builder.knowledgeNone": "No knowledge collections yet: create one under Knowledge, then choose it here.",
  "builder.knowledgeCollections": "The agent searches",
  "builder.knowledgeMissing": "not in this tenant",
  "builder.knowledgeTopK": "Passages per search",
  "builder.knowledgeTopKHint": "How many passages the agent reads each time it searches (default 4).",
  "builder.knowledgeAbout": "What they hold",
  "builder.knowledgeAboutHint": "Tells the agent when to search, e.g. refunds, delivery and our plans.",
  "builder.tools": "Tools",
  "builder.tool.endCall": "End the call",
  "builder.tool.hold": "Put the caller on hold",
  "builder.tool.dtmf": "Press keys (DTMF)",
  "builder.tool.time": "Look up the time",
  "builder.otherTools": "More tools",
  "builder.otherToolsHint": "Other tools are edited in the YAML view.",
  "builder.httpTool": "HTTP tool {name}",
  "builder.filler": "Filler",
  "builder.fillerHint": "Said if the tool has not answered within 0.8 seconds, e.g. \u201cOne moment while I check.\u201d",
  "builder.async": "Asynchronous",
  "builder.asyncHint": "The agent goes on talking; the result is told in a later reply.",
  "builder.httpToolsHint": "Add or remove HTTP tools, their endpoint and parameters, in the YAML view.",
  "builder.mcp": "MCP servers",
  "builder.mcpAdd": "Add an MCP server",
  "builder.mcpServer": "MCP server {server}",
  "builder.mcpUrl": "Server URL",
  "builder.mcpList": "List tools",
  "builder.mcpRemove": "Remove the MCP server {server}",
  "builder.mcpAllow": "Tools the agent may use",
  "builder.mcpToolName": "Tool name",
  "builder.mcpAllowOne": "Allow",
  "builder.mcpAllowHint": "List the server's tools, then choose the ones the agent may use: only those are offered.",
  "builder.realtimeTradeoffs": "A speech-to-speech model takes turns its own way, is priced per provider, and the call's audio goes to the provider's region. Disclosures and the greeting are said by the model unless a voice is set below.",
  "builder.phraseVoice": "A voice for the phrases said word for word",
  "builder.phraseVoiceHint": "The disclosures and the greeting are spoken by this TTS before the model talks, exactly as written.",
  "builder.earlyGeneration": "Early generation",
  "builder.earlyGenerationHint": "The reply is prepared while the speech-to-text provider confirms the end of the turn, and dropped if the caller goes on.",
  "builder.handoffs": "Handoffs",
  "builder.handoffsHint": "Other agents this one may hand the call to. The conversation carries over; the new agent answers in its own voice.",
  "builder.handoff": "Handoff {n}",
  "builder.handoffAgent": "Agent",
  "builder.handoffWhen": "When",
  "builder.handoffWhenHint": "In a few words, when to hand over, e.g. \u201cthe caller asks about a bill\u201d.",
  "builder.handoffAdd": "Add a handoff",
  "builder.handoffRemove": "Remove handoff {n}",
  "builder.handoffUnpublished": "{name} (not published)",
  "builder.flow": "Flow",
  "builder.flowNone": "No flow: the agent follows its instructions freely. Build one in the Flow view.",
  "builder.flowSummary": "A flow of {n} nodes, starting at {first}.",
  "builder.flowHint": "Edit it in the Flow view, or in the YAML.",
  "builder.transferTo": "Transfer to {key}",
  "builder.transfer.to": "Number or SIP address",
  "builder.transfer.mode": "How",
  "builder.transfer.blind": "Blind: the caller is connected at once",
  "builder.transfer.warm": "Warm: the agent stays on while the destination is called",
  "builder.transfer.whisper": "Whisper",
  "builder.transfer.whisperHint": "What the destination hears, in the agent's voice, before the caller is connected. {{call.from}} and {{vars.name}} work.",
  "builder.transfer.hint": "Destinations are added and removed in the YAML view; the agent may only transfer to these.",
  "builder.telephony": "Telephony",
  "builder.amd": "Answering machine detection",
  "builder.amdHint": "For calls the agent makes.",
  "builder.onMachine": "On an answering machine",
  "builder.machine.hangup": "Hang up",
  "builder.machine.voicemail": "Leave a voicemail",
  "builder.machine.continue": "Carry on",
  "builder.amdMode": "While it listens",
  "builder.amdMode.async": "Talk as soon as it sounds like a person",
  "builder.amdMode.sync": "Say nothing until it knows",
  "builder.voicemail": "Voicemail message",
  "builder.voicemailHint": "Said after the machine's beep, then the agent hangs up. The compliance profile may put the AI disclosure first, or not allow it.",
  "builder.dtmf": "Listen for key presses",
  "builder.recording": "Record calls",
  "builder.recordingHint": "The compliance profile may add a notice before the greeting.",
  "builder.consentPrompt": "Recording notice",
  "builder.postCall": "After the call",
  "builder.summary": "Write a summary",
  "builder.extractionSet": "Data extraction is set up. Edit its schema in the YAML view.",
  "builder.extractionNone": "No data extraction. Add a schema in the YAML view.",
  "builder.sentiment": "Judge the caller's sentiment",
  "builder.complianceQa": "Check compliance (disclosures, consent, opt-outs)",
  "builder.keyterms": "Key terms",
  "builder.keytermsHint": "Words the speech-to-text should listen for, comma-separated: names, products, amounts (Deepgram nova-3).",
  "builder.tags": "Outcomes to tag calls with",
  "builder.tagsHint": "Comma-separated, lowercase: resolved, callback_requested, promise_to_pay…",
  "builder.qaSet": "A QA rubric of {count, plural, one {# item} other {# items}} scores each call. Edit it in the YAML view.",
  "builder.qaNone": "No QA rubric. Add post_call.qa in the YAML view.",

  "calls.description": "Every call, newest first.",
  "calls.empty": "No calls yet",
  "calls.emptyBody": "Call a number that routes to an agent, or run swaralo call test demo.",
  "calls.col.when": "When",
  "calls.col.agent": "Agent",
  "calls.col.cost": "Cost",
  "calls.cost": "≈ {amount}",
  "calls.more": "Load more",
  "calls.filters": "Filter the calls",
  "calls.any": "Any",
  "calls.number": "Number (either party)",
  "calls.numberRule": "A number in international form, like +15550100100.",
  "calls.from": "From",
  "calls.to": "To",
  "calls.apply": "Apply",
  "calls.clear": "Clear",
  "calls.noneMatch": "No call matches",
  "calls.noneMatchBody": "Change or clear the filters.",
  "calls.status.completed": "Completed",
  "calls.status.transferred": "Transferred",
  "calls.status.busy": "Busy",
  "calls.status.no_answer": "No answer",
  "calls.status.canceled": "Canceled",
  "calls.status.rejected": "Rejected",
  "calls.status.blocked": "Blocked",
  "calls.status.failed": "Failed",
  "calls.status.voicemail": "Voicemail left",
  "calls.status.machine": "Machine answered",
  "call.title": "Call from {from} to {to}",
  "call.hangup": "Ended by",
  "live.title": "Live now",
  "live.titleOne": "A call in progress",
  "live.none": "No call is in progress.",
  "live.since": "Since",
  "live.state": "State",
  "live.inProgress": "In progress: it updates as it happens.",
  "live.state.connecting": "connecting…",
  "live.state.live": "live",
  "live.state.reconnecting": "reconnecting…",
  "live.state.closed": "not following",
  "calls.history": "History",
  "player.title": "Recording",
  "player.about": "{duration}, stereo: the caller on the left, the agent on the right.",
  "player.load": "Listen",
  "player.loading": "Getting the recording…",
  "player.audited": "Listening downloads the recording, and the download is recorded in the audit log.",
  "player.label": "The call's recording",
  "player.listen": "Listen to",
  "player.listen.both": "Both",
  "player.listen.caller": "The caller",
  "player.listen.agent": "The agent",
  "player.downloadCaller": "The caller's track",
  "player.downloadAgent": "The agent's track",
  "player.download": "Save the recording (WAV)",
  "player.notAllowed": "Your role cannot listen to recordings. Owners, admins and operators can.",
  "player.unavailable": "The recording is not available ({status}).",
  "inspect.timeline": "Timeline",
  "inspect.turns": "Turns",
  "inspect.transcript": "Transcript",
  "handoffs.title": "Handoffs",
  "handoffs.help": "Calls your agents hand to people, newest first, with what the person taking each should know. New ones appear as they happen.",
  "handoffs.none": "No call has been handed to a person yet.",
  "handoffs.reason": "Why",
  "handoffs.call": "The call",
  "handoffs.mode.warm": "warm",
  "handoffs.mode.blind": "blind",
  "handoffs.transcript": "What was said ({n} turns)",
  "handoffs.role.user": "Caller",
  "handoffs.role.assistant": "Agent",
  "monitor.title": "Supervise",
  "monitor.help": "Listen: hear both sides, unheard. Whisper: the person the caller is talking to hears you, the caller does not. Barge: both hear you. You can switch while you are on.",
  "monitor.listen": "Listen",
  "monitor.whisper": "Whisper",
  "monitor.barge": "Barge",
  "monitor.leave": "Leave",
  "monitor.state.connecting": "Joining…",
  "monitor.state.ringing": "Joining…",
  "monitor.state.in-call": "On the call",
  "monitor.state.ended": "Left the call",
  "monitor.state.failed": "Could not join the call",
  "inspect.cost": "Cost",
  "inspect.annotations": "After the call",
  "annotations.none": "No analyses: none were asked for (by the agent or the assist profile), or they are not done yet.",
  "annotations.kind.summary": "Summary",
  "annotations.kind.tags": "Outcome",
  "annotations.kind.sentiment": "Sentiment",
  "annotations.kind.qa_score": "QA score",
  "annotations.kind.compliance_qa": "Compliance QA",
  "annotations.kind.extraction": "Extracted data",
  "annotations.failed": "Not done: {why}",
  "annotations.skipped": "Skipped: {why}",
  "annotations.by": "Analysed by {model}.",
  "annotations.noTags": "None of the outcomes applied.",
  "annotations.sentiment": "{overall}, {trend} (score {score})",
  "annotations.qaTotal": "{total} of {max}",
  "annotations.turn": "Turn {turn}:",
  "annotations.compliancePassed": "Passed",
  "annotations.complianceFailed": "Failed: an opt-out the caller asked for was not recorded",
  "annotations.disclosed": "A disclosure was played",
  "annotations.notDisclosed": "No disclosure was played",
  "annotations.consentGiven": "Consent was asked for and given",
  "annotations.consentNotGiven": "Consent was asked for and not given",
  "annotations.consentNotAsked": "Consent was not asked for",
  "annotations.optOutHonoured": "The caller asked not to be called again; it was recorded",
  "annotations.optOutMissed": "The caller asked not to be called again; it was not recorded",
  "annotations.noOptOut": "No opt-out was asked for",
  "inspect.events": "Events (JSON)",
  "inspect.legend": "Stages",
  "inspect.compliance.profile": "Compliance profile",
  "inspect.tone.ok": "fine",
  "inspect.tone.warn": "worth a look",
  "inspect.tone.bad": "a problem",
  "inspect.tone.info": "note",
  "inspect.state": "{from} → {to}",
  "inspect.compliance": "Compliance at {point}: {outcome}",
  "inspect.actionRequested": "{type} asked for by {source}",
  "inspect.actionCompleted": "{type} done",
  "inspect.actionFailed": "{type} failed",
  "inspect.toolRunning": "Tool {tool}: no result",
  "inspect.toolOk": "Tool {tool}: done in {ms} ms",
  "inspect.toolFailed": "Tool {tool}: did not work ({error})",
  "inspect.sources": "Found in the knowledge base",
  "inspect.sourcesNone": "Nothing found in the knowledge base",
  "inspect.sourceScore": "score {score}",
  "inspect.fallback": "{role} fell back from {from} to {to}",
  "inspect.turn": "Turn {turn} · {ms} ms",
  "inspect.amd.human": "A person answered",
  "inspect.amd.unknown": "Not sure who answered: the agent talks",
  "inspect.amd.fax": "A fax machine answered",
  "inspect.amd.machine_start": "An answering machine answered",
  "inspect.amd.machine_end_beep": "An answering machine answered; its greeting ended with a beep",
  "inspect.amd.machine_end_silence": "An answering machine answered; its greeting ended in silence",
  "inspect.amd.machine_end_other": "An answering machine answered",
  "inspect.machineEnd.beep": "The machine's greeting ended with a beep: the voicemail starts",
  "inspect.machineEnd.silence": "The machine's greeting ended in silence: the voicemail starts",
  "inspect.machineEnd.other": "No end of the machine's greeting was heard: the voicemail starts anyway",
  "inspect.transfer.dialing": "Transfer: calling the destination; the agent stays with the caller",
  "inspect.transfer.answered": "Transfer: the destination answered",
  "inspect.transfer.whispering": "Transfer: the destination hears the whisper",
  "inspect.transfer.bridged": "Transfer: the caller is connected to the destination",
  "inspect.ended": "Call ended: {status}",
  "inspect.limit": "A tenant limit was reached",
  "inspect.flowNode": "Flow: {node}",
  "inspect.handoff": "Handed to {to}",
  "inspect.script.verb": "Script: {verb} ({outcome}, {ms} ms)",
  "inspect.script.hook": "Script hook: {kind}, HTTP {status} in {ms} ms",
  "inspect.script.hookFailed": "Script hook {kind} failed: {error}",
  "inspect.script.error": "Script error: {code}",
  "inspect.script.warning": "Script warning: {code}",
  "inspect.noTurns": "No turn was measured.",
  "inspect.targets": "Each turn from the end of the caller's speech to the agent's voice. Targets: {target} ms (p50), {limit} ms (p95); the line is {target} ms.",
  "inspect.interrupted": "interrupted",
  "inspect.cutOff": "cut off",
  "inspect.caller": "Caller",
  "inspect.agent": "Agent",
  "inspect.noTranscript": "This call has no transcript.",
  "inspect.noCost": "No cost was estimated for this call.",
  "inspect.estimate": "estimated from the price table {prices}, not an invoice",
  "inspect.costSplit": "The cost, by kind",
  "inspect.kind": "Kind",
  "inspect.quantity": "Used",
  "inspect.unpriced": "no price",
  "inspect.sip": "Signalling",
  "inspect.sip.far": "Far end",
  "inspect.sip.node": "Media node",
  "inspect.sip.received": "received",
  "inspect.sip.sent": "sent",
  "inspect.noSip": "No SIP signalling was recorded for this call.",
  "inspect.quality": "Audio quality",
  "inspect.noQuality": "The media node reported no audio statistics for this call.",
  "inspect.mos": "MOS {mos}",
  "inspect.codec": "Codec",
  "inspect.packetsIn": "Packets in",
  "inspect.packetsOut": "Packets out",
  "inspect.gaps": "Gaps",
  "inspect.jitter": "Jitter (max variance)",
  "inspect.qualityPct": "Quality",
  "inspect.qualityNote": "FreeSWITCH''s estimate from the audio it received, at hangup. Without RTCP there is no loss figure: gaps are packets it skipped over, which a caller sending audio in bursts also causes.",
  "inspect.kind.telephony": "Telephony",
  "inspect.kind.stt": "Speech to text",
  "inspect.kind.llmIn": "LLM input",
  "inspect.kind.llmOut": "LLM output",
  "inspect.kind.tts": "Text to speech",
  "inspect.stage.vad": "VAD",
  "inspect.stage.turn": "End of turn",
  "inspect.stage.stt": "STT",
  "inspect.stage.llm": "LLM",
  "inspect.stage.tool": "Tool",
  "inspect.stage.tts": "TTS",
  "calls.col.direction": "Direction",
  "calls.col.from": "From",
  "calls.col.to": "To",
  "calls.col.status": "Status",
  "calls.col.duration": "Duration",
  "calls.direction.inbound": "Inbound",
  "calls.direction.outbound": "Outbound",

  "numbers.description": "Phone numbers, and the agent or Call Script that answers each one.",
  "numbers.empty": "No phone numbers yet",
  "numbers.emptyBody": "Add a number with POST /v1/phone-numbers, then choose what answers it here.",
  "numbers.col.number": "Number",
  "numbers.col.agent": "Agent",
  "numbers.col.enabled": "Enabled",
  "numbers.unrouted": "Not routed",
  "numbers.col.answeredBy": "Answered by",
  "numbers.byAgent": "Agent: {name}",
  "numbers.byScript": "Call Script: {name}",
  "numbers.targetFor": "What answers {number}",
  "numbers.save": "Save",
  "numbers.saveFor": "Save what answers {number}",

  "trunks.description": "SIP trunks to your carriers.",
  "trunks.empty": "No SIP trunks yet",
  "trunks.emptyBody": "Connect a carrier with POST /v1/sip-trunks (a wizard with carrier presets comes next).",
  "trunks.col.name": "Name",
  "trunks.col.direction": "Direction",
  "trunks.col.auth": "Authentication",
  "trunks.col.enabled": "Enabled",
  "trunks.col.assist": "Assist profile",
  "trunks.notRecording": "Not a recording source",
  "trunks.noAssist": "None",
  "trunks.assistFor": "Assist profile for {name}",

  "assist.description": "Help for your human agents as they talk: what the knowledge base says, a next step, and what they must still say.",
  "assist.live": "Assisted calls now",
  "assist.none": "No assisted call is in progress",
  "assist.noneBody": "A call is assisted when its SIPREC trunk names an assist profile (Trunks), or a Call Script transcribes it with assist: <profile>.",
  "assist.profile": "Profile",
  "assist.follow": "Follow",
  "assist.followOne": "Follow the call from {from}",
  "assist.following": "Opening the call…",
  "assist.call": "The call, as it is spoken",
  "assist.stop": "Stop following",
  "assist.ended": "The call has ended.",
  "assist.record": "Open its record",
  "assist.transcript": "Transcript",
  "assist.nothingSaid": "Nothing has been said yet.",
  "assist.speaker.customer": "Customer",
  "assist.speaker.agent": "Agent",
  "assist.nudges": "To say",
  "assist.noNudges": "Nothing to say yet.",
  "assist.nudge.due": "Due",
  "assist.nudge.done": "Said",
  "assist.suggestions": "Suggestions",
  "assist.noSuggestions": "Suggestions appear after the customer speaks.",
  "assist.fromKnowledge": "From {title} · {score} % match",
  "assist.nextStep": "Next step",
  "assist.why": "Why: {why}",
  "assist.open": "Open {title}",
  "assist.profiles": "Assist profiles",
  "assist.profiles.intro": "How your agents are helped: the knowledge searched, a next step, what they must say, and the review after each call. A SIPREC trunk names one.",
  "assist.profiles.empty": "No assist profiles yet",
  "assist.profiles.emptyBody": "Write one to help the agents on a recording source's calls.",
  "assist.profiles.new": "New profile",
  "assist.profiles.edit": "Edit",
  "assist.profiles.editOne": "Edit {name}",
  "assist.profiles.remove": "Delete {name}",
  "assist.profiles.removeConfirm": "Delete the assist profile {name}? Trunks that name it stop assisting.",
  "assist.profiles.yaml": "The profile (YAML)",
  "assist.profiles.yamlHint": "kind: AssistProfile, with metadata.name and spec. The guide lists every field.",
  "assist.profiles.yamlBroken": "Not YAML yet: {why}",
  "assist.profiles.refused": "The profile was not saved:",
  "assist.profiles.save": "Save profile",
  "assist.profiles.name": "Name",
  "assist.profiles.knowledge": "Knowledge",
  "assist.profiles.nudges": "Nudges",
  "assist.profiles.updated": "Updated",
  "assist.profiles.actions": "Actions",
  "operators.description": "The people your campaigns call on: who is ready, and the contact offered to you.",
  "operators.empty": "No operators yet",
  "operators.emptyBody": "Mark a SIP user as an operator on the SIP users page, then register its phone.",
  "operators.all": "All operators",
  "operators.col.username": "Username",
  "operators.col.name": "Name",
  "operators.col.state": "State",
  "operators.col.devices": "Devices",
  "operators.state.offline": "Offline",
  "operators.state.ready": "Ready",
  "operators.state.not_ready": "Not ready",
  "operators.state.busy": "On a call",
  "operators.state.wrap_up": "Wrap-up",
  "operators.atWork": "At work",
  "operators.me": "I am",
  "operators.choose": "Choose an operator",
  "operators.now": "Now: {state}",
  "operators.offlineHint": "Register your phone or softphone with your SIP username to go online.",
  "operators.ready": "Ready",
  "operators.notReady": "Not ready",
  "operators.noOffer": "No contact offered to you right now.",
  "operators.offer": "The contact offered to you",
  "operators.offerFrom": "Call for {campaign}?",
  "operators.number": "Number",
  "operators.left": "{seconds} s left to decide",
  "operators.call": "Call",
  "operators.later": "Skip for now",
  "operators.never": "Don't call",
  "operators.calling": "Calling: your phone rings first, then the contact.",
  "operators.onLine": "On the line for predictive campaigns: contacts are put through to you as they answer, with a beep.",
  "operators.endCall": "End this call",
  "sipUsers.description": "Phones and browsers that register to your edge, call your agents, and take calls they hand over.",
  "sipUsers.empty": "No SIP users yet",
  "sipUsers.emptyBody": "Add one above, then point a softphone at the edge with its username and password.",
  "sipUsers.new": "New SIP user",
  "sipUsers.col.username": "Username",
  "sipUsers.col.name": "Display name",
  "sipUsers.col.enabled": "Enabled",
  "sipUsers.password": "Password",
  "sipUsers.add": "Add",
  "sipUsers.operator": "Operator (works campaigns)",
  "sipUsers.usernameRule": "3 to 64 lowercase letters, digits, dots, dashes or underscores, starting with a letter or digit.",
  "sipUsers.passwordRule": "At least 12 characters.",
  "sipUsers.taken": "That username is taken: usernames are unique across the installation.",
  "sipUsers.delete": "Delete",
  "sipUsers.deleteFor": "Delete {username}",

  "hold.on": "On legal hold since {when}: {reason}",
  "hold.place": "Place a legal hold",
  "hold.reason": "Reason",
  "hold.hint": "The call's recording, transcript and record are kept, whatever their retention, until the hold is released. An erasure leaves them too.",
  "hold.confirm": "Hold",
  "hold.release": "Release the hold",
  "hold.releaseConfirm": "Release the legal hold? The call's data is then kept only as long as its retention says; what is overdue is deleted at the next sweep.",

  "knowledge.description": "Documents your agents answer from: upload files, add pages or text, and try a search.",
  "knowledge.new": "New collection",
  "knowledge.empty": "No knowledge collections yet",
  "knowledge.emptyBody": "A collection holds documents an agent searches during calls: policies, prices, how-tos.",
  "knowledge.collections": "Knowledge collections",
  "knowledge.name": "Name",
  "knowledge.descriptionLabel": "Description",
  "knowledge.embedder": "Embedder",
  "knowledge.embedderHint": "Turns text into vectors (1024 dimensions). It is tried before the collection is created.",
  "knowledge.provider.openai": "OpenAI",
  "knowledge.provider.openai-compatible": "OpenAI-compatible server (TEI, vLLM, Ollama)",
  "knowledge.provider.endpoint": "One of your model endpoints",
  "knowledge.endpoint": "Model endpoint",
  "knowledge.endpointHint": "An embedding model registered under Models; it must produce 1024 dimensions.",
  "knowledge.provider.fake": "Test embedder (offline, word matching)",
  "knowledge.model": "Model",
  "knowledge.credential": "Credential",
  "knowledge.credentialHint": "The name of a credential with your OpenAI api_key (Settings, Credentials).",
  "knowledge.credentialHintCompatible": "The name of a credential with the server's base_url, and an api_key if it needs one.",
  "knowledge.askDimensions": "Ask for 1024 dimensions",
  "knowledge.askDimensionsHint": "For a model that shortens its vectors on request, such as gemini-embedding-001. Leave it off for bge-m3 and other fixed-size models.",
  "knowledge.checking": "Checking the embedder…",
  "knowledge.create": "Create collection",
  "knowledge.documents": "Documents",
  "knowledge.chunks": "Passages",
  "knowledge.updated": "Updated",
  "knowledge.subtitle": "{documents, plural, one {# document} other {# documents}}, {chunks, plural, one {# passage} other {# passages}}",
  "knowledge.embeddedWith": "Embedded with {embedder}",
  "knowledge.reindex": "Re-index",
  "knowledge.reindexConfirm": "Read every document again? Searches use the current passages until each document is done.",
  "knowledge.reindexed": "{n, plural, one {# document is} other {# documents are}} being indexed again.",
  "knowledge.delete": "Delete collection",
  "knowledge.deleteConfirm": "Delete {name}, its documents and their files? Agents that search it will find nothing.",
  "knowledge.noDocuments": "No documents yet: add a file, a page or text above.",
  "knowledge.title": "Title",
  "knowledge.source": "Source",
  "knowledge.source.file": "File",
  "knowledge.source.url": "Web page",
  "knowledge.source.text": "Text",
  "knowledge.statusLabel": "Status",
  "knowledge.status.pending": "Waiting",
  "knowledge.status.processing": "Reading",
  "knowledge.status.ready": "Ready",
  "knowledge.status.failed": "Failed",
  "knowledge.size": "Size",
  "knowledge.added": "Added",
  "knowledge.actions": "Actions",
  "knowledge.remove": "Delete",
  "knowledge.removeFor": "Delete {title}",
  "knowledge.removeConfirm": "Delete {title} and its passages?",
  "knowledge.add": "Add a document",
  "knowledge.addFrom": "Add from",
  "knowledge.files": "Files",
  "knowledge.filesHint": "PDF, Word (DOCX), HTML, Markdown, text or CSV, up to 20 MB each.",
  "knowledge.url": "Page address",
  "knowledge.followLinks": "Also read the pages it links to on the same site (one level, 20 pages at most)",
  "knowledge.text": "Text",
  "knowledge.addButton": "Add",
  "knowledge.added.pending": "Added: it is searchable once it shows Ready.",
  "knowledge.search": "Try a search",
  "knowledge.query": "Question",
  "knowledge.queryPlaceholder": "How long does a refund take?",
  "knowledge.topK": "Results",
  "knowledge.searchButton": "Search",
  "knowledge.results": "Search results",
  "knowledge.nothingFound": "Nothing found.",
  "knowledge.took": "Found in {ms} ms",
  "knowledge.score": "score {score} · by meaning #{vector} · by words #{text}",
  "knowledge.where.page": "page {page}",
  "knowledge.where.heading": "under {heading}",
  "knowledge.where.row": "row {row}",

  "builder.onThisPage": "On this page",
  "builder.greetingClip": "Recording",
  "builder.greetingClipHint": "Plays one of your clips in place of the text, after any disclosures. The text, if given, is what it says.",
  "builder.greetingClipNone": "None: the voice says the text",

  "campaigns.safety.title": "Safety valves",
  "campaigns.safety.about": "Swaralo pauses the campaign itself rather than call contact after contact into a fault. Busy and unanswered calls are no fault; a call that fails, or a provider that fails in it, is.",
  "campaigns.safety.onAuth": "Pause when a provider refuses the key or the account's billing (it would refuse every call)",
  "campaigns.safety.onFailures": "Pause when too many recent calls fail",
  "campaigns.safety.pct": "Failed calls (%)",
  "campaigns.safety.minutes": "Of the last (minutes)",
  "campaigns.safety.calls": "Once there are (calls)",
  "campaigns.safety.save": "Save safety valves",
  "campaigns.safety.saved": "Saved.",
  "campaigns.safety.paused.failure_rate": "Swaralo paused this campaign: too many of its calls failed.",
  "campaigns.safety.paused.provider_auth": "Swaralo paused this campaign: a provider refused its key or billing.",
  "campaigns.safety.pausedHint": "Fix the cause (a trunk, a credential, the provider account), then resume it.",

  "clips.description": "Recorded audio your agents can play: a greeting, a disclosure in a person's voice, a hold message.",
  "clips.add": "Add a clip",
  "clips.empty": "No clips yet",
  "clips.emptyBody": "Upload a recording, or record one here. Agents name a clip to play it.",
  "clips.table": "Clips",
  "clips.name": "Name",
  "clips.nameHint": "How agents name it: lowercase letters, digits, - and _, for example greeting-hi.",
  "clips.title": "Title",
  "clips.length": "Length",
  "clips.transcript": "What it says",
  "clips.transcriptHint": "Goes into the call's transcript when the clip plays.",
  "clips.actions": "Actions",
  "clips.addFrom": "Add from",
  "clips.source.file": "A file",
  "clips.source.record": "Record here",
  "clips.file": "File (WAV, FLAC, Ogg or MP3, up to two minutes)",
  "clips.record": "Record",
  "clips.recordAgain": "Record again",
  "clips.stop": "Stop",
  "clips.recording": "Recording: {time} (up to {max} s)",
  "clips.recorded": "Recorded {time}.",
  "clips.preview": "The recording",
  "clips.micDenied": "The browser did not allow the microphone. Allow it for this site, then try again.",
  "clips.upload": "Save clip",
  "clips.cancel": "Cancel",
  "clips.play": "Play {name}",
  "clips.playing": "{name}",
  "clips.edit": "Edit {name}",
  "clips.editing": "Edit {name}",
  "clips.save": "Save",
  "clips.renameNote": "Agents that name the old name will no longer find it.",
  "clips.delete": "Delete {name}",
  "clips.deleteConfirm": "Delete the clip {name}? Agents that play it will no longer find it.",
  "scripts.description": "Answer numbers with verbs your server returns: menus, routing, dialling out, and handing calls to an agent or your own AI.",
  "scripts.apps": "Call Script apps",
  "scripts.new": "New Call Script",
  "scripts.empty": "No Call Scripts yet",
  "scripts.emptyBody": "A Call Script app answers a number with verbs from your webhook: say, gather, dial, agent, stream and more.",
  "scripts.name": "Name",
  "scripts.descriptionLabel": "Description",
  "scripts.live": "Live",
  "scripts.latest": "Latest",
  "scripts.updated": "Updated",
  "scripts.notPublished": "Not published",
  "scripts.callHook": "Call hook",
  "scripts.method": "Method",
  "scripts.statusHook": "Status hook (optional)",
  "scripts.dialect": "Verb format",
  "scripts.dialect.swaralo": "Swaralo",
  "scripts.dialect.jambonz": "jambonz 0.9 compatible",
  "scripts.dialectHint": "Choose jambonz 0.9 to run an app written for that format unchanged.",
  "scripts.language": "Language",
  "scripts.create": "Create Call Script",
  "scripts.open": "Open {name}",
  "scripts.secret.title": "Keep this webhook secret now",
  "scripts.secret.body": "It signs every request Swaralo sends your hooks (Standard Webhooks). It is shown only this once.",
  "scripts.secret.label": "Webhook secret",
  "scripts.secret.copy": "Copy",
  "scripts.secret.copied": "Copied",
  "scripts.rotate": "Rotate secret",
  "scripts.rotateConfirm": "Make a new webhook secret? The current one keeps signing for a day, so your server can move to the new one.",
  "scripts.delete": "Delete",
  "scripts.deleteConfirm": "Delete {name}? Numbers routed to it must be routed elsewhere first.",
  "scripts.liveVersion": "Calls run version {version}.",
  "scripts.noneLive": "No version is published: calls to its numbers are refused.",
  "scripts.versions": "Versions",
  "scripts.publish": "Publish",
  "scripts.publishOne": "Publish version {version}",
  "scripts.test": "Test call hook",
  "scripts.testOne": "Test the call hook of version {version}",
  "scripts.test.running": "Calling the hook with a sample call…",
  "scripts.test.of": "Version {version}'s call hook",
  "scripts.test.result": "Hook test result",
  "scripts.test.ok": "It answered in {ms} ms with verbs a call would run.",
  "scripts.test.failed": "It did not answer usably: {error}",
  "scripts.test.badVerbs": "It answered, but a call would refuse its verbs ({n} problems).",
  "scripts.test.detail": "{url}: HTTP {status}, {attempts} attempt(s)",
  "scripts.test.problems": "Problems",
  "scripts.test.problem": "verb {index}, {path}: {message}",
  "scripts.test.warnings": "Ignored attributes",
  "scripts.test.answer": "What it answered",
  "scripts.numbers": "Numbers",
  "scripts.noNumbers": "No number routes to this app yet. Route one on the",
  "scripts.newVersion": "New version",
  "scripts.spec": "Spec (JSON)",
  "scripts.specHint": "Starts from version {version}. Saving makes a draft; publish it to put it live.",
  "scripts.specInvalid": "This is not a JSON object yet.",
  "scripts.saveVersion": "Save as a new version",
  "scripts.saved": "Saved as version {version}, a draft.",

  "campaigns.description": "Call a list of contacts with an agent, at your pace, under the rules of the campaign's type.",
  "campaigns.new": "New campaign",
  "campaigns.empty": "No campaigns yet",
  "campaigns.emptyBody": "A campaign calls each of its contacts with one of your agents. Set one up with a list of numbers.",
  "campaigns.col.name": "Name",
  "campaigns.col.type": "Type",
  "campaigns.col.status": "Status",
  "campaigns.col.contacts": "Contacts",
  "campaigns.col.started": "Started",
  "campaigns.col.outcome": "Outcome",
  "campaigns.col.attempts": "Calls",
  "campaigns.col.next": "Next try",
  "campaigns.col.call": "Call",
  "campaigns.col.why": "Why",
  "campaigns.status.draft": "Draft",
  "campaigns.status.running": "Running",
  "campaigns.status.paused": "Paused",
  "campaigns.status.completed": "Completed",
  "campaigns.status.stopped": "Stopped",
  "campaigns.contact.pending": "Waiting",
  "campaigns.contact.queued": "Queued",
  "campaigns.contact.in_progress": "On a call",
  "campaigns.contact.deferred": "Deferred",
  "campaigns.contact.done": "Done",
  "campaigns.contact.failed": "Not reached",
  "campaigns.contact.blocked": "Blocked",
  "campaigns.contact.suppressed": "Do not call",
  "campaigns.outcome.answered": "Answered",
  "campaigns.outcome.voicemail": "Voicemail left",
  "campaigns.outcome.machine": "Answering machine",
  "campaigns.outcome.busy": "Busy",
  "campaigns.outcome.no_answer": "No answer",
  "campaigns.outcome.failed": "Failed",
  "campaigns.outcome.blocked": "Blocked by the rules",
  "campaigns.outcome.abandoned": "Abandoned (no one free to talk)",
  "campaigns.outcome.skipped": "Skipped by an operator",
  "campaigns.redial.title": "Call some contacts again",
  "campaigns.redial.intro": "A new draft with this campaign's settings and the contacts whose last outcome you pick, with their variables. Contacts who opted out are never taken.",
  "campaigns.redial.outcomes": "Last outcome",
  "campaigns.redial.button": "Create a draft ({count, plural, one {# contact} other {# contacts}})",
  "campaigns.action.start": "Start",
  "campaigns.action.pause": "Pause",
  "campaigns.action.resume": "Resume",
  "campaigns.action.stop": "Stop",
  "campaigns.stopConfirm": "Stop the campaign for good? Calls in progress finish; nobody else is called, and it cannot be resumed.",
  "campaigns.export": "Export contacts",
  "campaigns.subtitle": "{type} · {contacts, plural, one {# contact} other {# contacts}}",
  "campaigns.where": "Where it stands",
  "campaigns.progress": "Contacts finished",
  "campaigns.progressText": "{done} of {total} contacts finished",
  "campaigns.calls": "Calls made",
  "campaigns.outcomes": "Last outcomes:",
  "campaigns.nextDeferral": "The first deferred contact may be called at {when}.",
  "campaigns.contacts": "Contacts",
  "campaigns.filter": "Show",
  "campaigns.allStatuses": "All",
  "campaigns.openCall": "Open",
  "campaigns.dry.title": "What the rules would decide at {when}",
  "campaigns.dry.running": "Checking every contact against the rules…",
  "campaigns.dry.allowed": "{n} of {total} would be called",
  "campaigns.dry.deferred": "{n} would wait, the first until {when}",
  "campaigns.dry.blocked": "{n} would not be called: {rules}",
  "campaigns.dry.warnings": "Warnings: {rules}",
  "campaigns.dry.rule": "{rule} {n}",
  "campaigns.dry.examples.blocked": "Contacts that would not be called",
  "campaigns.dry.examples.deferred": "Contacts that would wait",
  "campaigns.wizard.title": "New campaign",
  "campaigns.wizard.intro": "Set it up step by step; it is saved as a draft as you go, and starts only when you say so.",
  "campaigns.wizard.steps": "Steps",
  "campaigns.wizard.next": "Save and continue",
  "campaigns.wizard.back": "Back",
  "campaigns.wizard.later": "Start later",
  "campaigns.step.basics": "The campaign",
  "campaigns.step.contacts": "Contacts",
  "campaigns.step.schedule": "Schedule and pace",
  "campaigns.step.compliance": "Compliance",
  "campaigns.step.review": "Review and start",
  "campaigns.f.name": "Name",
  "campaigns.f.type": "Campaign type",
  "campaigns.f.typeHint": "The type the compliance rules name: promotional, service, transactional, debt collection…",
  "campaigns.s.hoursFrom": "Call from",
  "campaigns.s.hoursUntil": "Call until",
  "campaigns.s.hoursHint": "In each contact's own time, inside the hours the rules allow. Leave empty for the rules' hours.",
  "campaigns.s.ring": "A contact's phone rings for (seconds)",
  "campaigns.s.ringHint": "The US and the UK ask at least 15 seconds.",
  "campaigns.funnel.title": "How far the contacts got",
  "campaigns.funnel.contacts": "Contacts",
  "campaigns.funnel.dialled": "Dialled",
  "campaigns.funnel.answered": "Answered",
  "campaigns.funnel.person": "A person answered",
  "campaigns.funnel.talked": "Talked to",
  "campaigns.funnel.goalOf": "Goal: {tag}",
  "campaigns.funnel.goal": "Goal (an outcome tag)",
  "campaigns.funnel.goalHint": "A call reaches the goal when its post-call analysis, or its Call Script, tags it so. Empty: no goal.",
  "campaigns.funnel.save": "Save the goal",
  "campaigns.f.talker": "Who talks",
  "campaigns.mode.agent": "An AI agent",
  "campaigns.mode.agentHint": "The agent calls each contact itself.",
  "campaigns.mode.progressive": "Operators: progressive",
  "campaigns.mode.progressiveHint": "A ready operator's phone rings first; then the contact is dialled and put through.",
  "campaigns.mode.preview": "Operators: preview",
  "campaigns.mode.previewHint": "A ready operator sees the contact first and decides whether to call.",
  "campaigns.mode.predictive": "Operators: predictive",
  "campaigns.mode.predictiveHint": "Contacts are dialled ahead of the operators, at the pace that keeps them busy; each who answers goes to the operator free longest. Operators stay on a line their phone answers by itself.",
  "campaigns.d.target": "Abandoned calls to aim at (%)",
  "campaigns.d.targetHint": "Of the contacts who answer, the share no operator takes in 2 s. The US and UK allow 3 %; the pace stays under it.",
  "campaigns.d.ratio": "Calls at most per operator",
  "campaigns.d.ratioHint": "How many contacts may ring for each operator about to be free.",
  "campaigns.pace": "Pace",
  "campaigns.pace.intro": "The last half hour of calls (at least the last 50), and the operators now.",
  "campaigns.pace.answerRate": "Answered by a person",
  "campaigns.pace.handle": "Handle time (talk and wrap-up)",
  "campaigns.pace.seconds": "{s} s",
  "campaigns.pace.abandoned": "Abandoned",
  "campaigns.pace.ofTarget": "{rate} (aiming at {target})",
  "campaigns.pace.perOperator": "Calls in progress per operator on a line",
  "campaigns.pace.onLine": "Operators on a line",
  "campaigns.pace.operators": "{on}: {waiting} waiting, {talking} talking, {wrapping} wrapping up",
  "campaigns.pace.calls": "Calls in the window",
  "campaigns.pace.window": "{placed} placed, {answered} answered, {abandoned} abandoned",
  "campaigns.pace.warming": "Until 30 calls are in, it dials one contact per waiting operator.",
  "campaigns.pace.over": "Above its aim: fewer contacts are dialled ahead until the rate falls back.",
  "campaigns.pace.noLines": "No operator is on a line yet: a ready operator's phone is called and answers by itself.",
  "campaigns.f.operators": "Operators",
  "campaigns.f.noOperators": "No operators yet: mark a SIP user as an operator first.",
  "campaigns.step.dialer": "Operators",
  "campaigns.d.ring": "Operator's phone rings for (seconds)",
  "campaigns.d.timeout": "Time to decide on a contact (seconds)",
  "campaigns.d.onTimeout": "If the operator does not decide",
  "campaigns.d.skip": "Offer the contact to another operator",
  "campaigns.d.dial": "Call the contact",
  "campaigns.d.message": "Message when no operator can take the call",
  "campaigns.d.messageHint": "Required in the US and the UK: who is calling, and a number to call back.",
  "campaigns.d.voice": "Its voice",
  "campaigns.d.provider": "Speech provider",
  "campaigns.d.voiceName": "Voice",
  "campaigns.d.credential": "Credential",
  "campaigns.d.noCredential": "None",
  "campaigns.d.voiceNeeded": "A message needs a provider and a voice to say it.",
  "campaigns.f.agent": "Agent",
  "campaigns.f.agentHint": "Its published version is pinned when the campaign starts.",
  "campaigns.f.choose": "Choose…",
  "campaigns.f.from": "Caller IDs",
  "campaigns.f.fromHint": "Your numbers the calls show, used in turn.",
  "campaigns.f.trunk": "Trunk",
  "campaigns.f.ownTrunk": "Each number's own",
  "campaigns.c.intro": "A CSV file with a header row: one column with the number, any others become the contact's variables in the agent's prompts.",
  "campaigns.c.file": "Contacts file (CSV)",
  "campaigns.c.column": "The column with the number",
  "campaigns.c.country": "Country for national numbers",
  "campaigns.c.countryHint": "Two letters, e.g. IN: a number without +country is read as that country's.",
  "campaigns.c.variables": "Variables from the other columns: {list}",
  "campaigns.c.import": "Import",
  "campaigns.c.imported": "{added, plural, one {# contact added} other {# contacts added}}, {updated} updated.",
  "campaigns.c.count": "{n, plural, =0 {No contacts yet} one {# contact in the campaign} other {# contacts in the campaign}}",
  "campaigns.s.start": "Start",
  "campaigns.s.startHint": "Empty: as soon as it is started.",
  "campaigns.s.end": "End",
  "campaigns.s.concurrency": "Calls at once",
  "campaigns.s.cps": "New calls per second",
  "campaigns.s.attempts": "Tries per contact",
  "campaigns.s.delay": "Minutes before trying again",
  "campaigns.s.retryOn": "Try again when the call was",
  "campaigns.s.amd": "Answering machines",
  "campaigns.s.amdOn": "Detect answering machines (instead of the agent's setting)",
  "campaigns.s.onMachine": "When a machine answers",
  "campaigns.s.hangup": "Hang up",
  "campaigns.s.leave": "Leave a message",
  "campaigns.s.message": "The message",
  "campaigns.s.messageHint": "Said after the beep; {{vars.name}} and the other variables are filled in.",
  "campaigns.k.profile": "The {name} rules apply to your calls; for a {type} campaign they ask for the fields below.",
  "campaigns.k.consent": "Each contact needs a consent for {type} calls ({kinds}). The review shows how many have one.",
  "campaigns.k.noConsent": "{type} calls need no consent under these rules.",
  "campaigns.k.noFields": "No campaign fields are required.",
  "campaigns.k.list": "Several values, separated by commas.",
  "campaigns.k.missing": "Still missing: {list}. Calls are refused without them.",

  "compliance.description": "The calling rules that apply to your calls, why a call is allowed or not, and the consents and do-not-call list behind them.",
  "compliance.tabs": "Compliance views",
  "compliance.tab.rules": "Rules",
  "compliance.tab.simulator": "Simulator",
  "compliance.tab.consents": "Consents",
  "compliance.tab.dnc": "Do-not-call list",
  "compliance.number": "Number",
  "compliance.e164": "A number in international format, like +919876543210",
  "compliance.find": "Find",
  "compliance.more": "Show more",
  "compliance.rules.profile": "Profile",
  "compliance.rules.yours": "The one that applies to you",
  "compliance.chosen.request": "{name} ({id}, version {version}).",
  "compliance.chosen.tenant": "{name} ({id}, version {version}): the profile your tenant is set to.",
  "compliance.chosen.country": "{name} ({id}, version {version}): your tenant's country. Calls to another country follow that country's profile.",
  "compliance.chosen.default": "{name} ({id}, version {version}): no profile matches your tenant's country, so the global default applies.",
  "compliance.rules.overridden": "Your tenant has changed some rules (changes {hash}).",
  "compliance.rules.notOverridden": "Your tenant has not changed any rule.",
  "compliance.rules.refused": "Your tenant's changes are not applied here: {why}",
  "compliance.changes.title": "Your changes",
  "compliance.changes.intro": "Make the rules stricter for your calls. Each rule below says what you may do with it: make it stricter, change it, or nothing (locked). A change that would loosen a rule is refused whole, and nothing is saved.",
  "compliance.changes.hours": "Calling hours",
  "compliance.changes.start": "Calls from",
  "compliance.changes.end": "Calls until",
  "compliance.changes.saveHours": "Save calling hours",
  "compliance.changes.hoursHint": "In the called person's local time, on the days the profile allows. They must fall within the profile's hours.",
  "compliance.changes.advanced": "Change other rules (JSON)",
  "compliance.changes.json": "Your overrides, by section",
  "compliance.changes.jsonHint": "For example {\"frequency_cap\": {\"enabled\": true, \"per_day\": 2}}. Saving replaces all your overrides.",
  "compliance.changes.notJson": "This is not valid JSON.",
  "compliance.changes.notObject": "Write an object, by section: { \"section\": { ... } }.",
  "compliance.changes.save": "Save overrides",
  "compliance.changes.clear": "Remove all my changes",
  "compliance.changes.refused": "Not saved: {why}",
  "compliance.changes.saved": "Saved. New calls follow your changes within 30 seconds.",
  "compliance.rules.notAdvice": "The rules encode published regulation and product defaults, each with its sources. They are not legal advice.",
  "compliance.rules.changed": "Changed by your tenant",
  "compliance.rules.profileSays": "The profile says:",
  "compliance.rules.sources": "{n, plural, one {# source} other {# sources}}",
  "compliance.rules.none": "None",
  "compliance.rules.yes": "Yes",
  "compliance.rules.no": "No",
  "compliance.may.none": "Locked",
  "compliance.may.narrow": "You may make it stricter",
  "compliance.may.override": "You may change it",
  "compliance.tag.V": "Primary source",
  "compliance.tag.S": "Secondary source",
  "compliance.tag.I": "Inference",
  "compliance.tag.P": "Product default",
  "compliance.tag.R": "Research requirement",
  "compliance.sim.intro": "Describe a call to see what the rules decide and why. Your tenant's settings apply, and the number's consents, do-not-call lists and recent calls are looked up as for a real call. Nothing is dialled.",
  "compliance.sim.point": "When",
  "compliance.point.outbound.originate": "Before dialling out",
  "compliance.point.inbound.accept": "Answering an incoming call",
  "compliance.point.action.transfer": "Transferring a call",
  "compliance.point.recording.start": "Starting a recording",
  "compliance.point.voicemail.drop": "Leaving a voicemail",
  "compliance.sim.to": "Called number",
  "compliance.sim.from": "Caller ID",
  "compliance.sim.campaign": "Campaign type",
  "compliance.sim.campaignHint": "Leave empty for a call made through the API, which is not a campaign.",
  "compliance.sim.when": "Time",
  "compliance.sim.whenHint": "In your time zone; now if left empty.",
  "compliance.sim.language": "Language",
  "compliance.sim.recording": "The call is recorded",
  "compliance.sim.legs": "The transfer",
  "compliance.sim.leg.from": "The call comes from",
  "compliance.sim.leg.to": "It goes to",
  "compliance.leg.pstn": "The phone network",
  "compliance.leg.sip": "A SIP address",
  "compliance.leg.internal": "Inside Swaralo",
  "compliance.sim.fields": "Campaign fields (JSON)",
  "compliance.sim.fieldsHint": "The registration and declaration fields a campaign carries, if any.",
  "compliance.sim.fieldsJson": "Not valid JSON",
  "compliance.sim.fieldsObject": "A JSON object of field names and values",
  "compliance.sim.run": "Check",
  "compliance.outcome.allow": "Allowed",
  "compliance.outcome.deny": "Not allowed",
  "compliance.outcome.defer": "Not now",
  "compliance.sim.deferred": "Not now: it may be tried at {when}",
  "compliance.sim.under": "Under {id}, version {version}.",
  "compliance.sim.why": "Why",
  "compliance.sim.warnings": "Warnings",
  "compliance.sim.then": "The call then",
  "compliance.sim.play": "plays {key} ({text}, in {language})",
  "compliance.sim.noPlay": "plays no announcement",
  "compliance.sim.retention": "is kept for (days): {list}",
  "compliance.sim.tools": "gives the agent {list}, whether it declares it or not",
  "compliance.csv.import": "Import CSV",
  "compliance.csv.export": "Export CSV",
  "compliance.csv.problems": "{n, plural, one {# line was not imported:} other {# lines were not imported:}}",
  "compliance.csv.line": "Line {line}: {error}",
  "compliance.consents.search": "Find consents",
  "compliance.consents.purpose": "Purpose",
  "compliance.consents.kind": "Kind",
  "compliance.consents.given": "Given",
  "compliance.consents.status": "Status",
  "compliance.consents.all": "All",
  "compliance.consents.active": "Active",
  "compliance.consents.revoked": "Revoked",
  "compliance.consents.revokedOn": "Revoked {when}",
  "compliance.consents.until": "Until {when}",
  "compliance.consents.actions": "Actions",
  "compliance.consents.revoke": "Revoke",
  "compliance.consents.revokeFor": "Revoke the {purpose} consent of {number}",
  "compliance.consents.revokeConfirm": "Revoke the {purpose} consent of {number}? A new one may not count for a while after this.",
  "compliance.consents.empty": "No consents found",
  "compliance.consents.emptyBody": "Consents are recorded through the API or imported from a CSV file.",
  "compliance.consents.imported": "{imported, plural, one {# consent imported} other {# consents imported}}, {skipped} already there.",
  "compliance.kind.explicit": "Explicit",
  "compliance.kind.inferred": "Inferred",
  "compliance.evidence.view": "Evidence",
  "compliance.evidence.viewFor": "The evidence of the {purpose} consent of {number}",
  "compliance.evidence.title": "Evidence",
  "compliance.evidence.of": "The proof of the {purpose} consent of {number}. Opening it is recorded in the audit log.",
  "compliance.evidence.image": "The consent's evidence",
  "compliance.evidence.audio": "The consent's recording",
  "compliance.evidence.pdf": "The consent's document",
  "compliance.evidence.type": "Type",
  "compliance.evidence.size": "Size",
  "compliance.evidence.bytes": "{n, number} bytes",
  "compliance.evidence.download": "Download",
  "compliance.dnc.search": "Look a number up",
  "compliance.dnc.add": "Add a number",
  "compliance.dnc.number": "Number to add",
  "compliance.dnc.reason": "Reason",
  "compliance.dnc.addButton": "Add to the list",
  "compliance.dnc.source": "Added by",
  "compliance.dnc.from.api": "The API or dashboard",
  "compliance.dnc.from.import": "An import",
  "compliance.dnc.from.opt_out": "The caller, on a call",
  "compliance.dnc.from.complaint": "A complaint",
  "compliance.dnc.from.other": "Other",
  "compliance.dnc.added": "Added",
  "compliance.dnc.until": "Until",
  "compliance.dnc.forever": "No end",
  "compliance.dnc.remove": "Remove",
  "compliance.dnc.removeFor": "Remove {number} from the list",
  "compliance.dnc.removeConfirm": "Take {number} off the do-not-call list? It may be called again.",
  "compliance.dnc.empty": "The list is empty",
  "compliance.dnc.notListed": "{number} is not on the list",
  "compliance.dnc.emptyBody": "Numbers are added here, through the API, by an import, or when a caller asks not to be called again.",
  "compliance.dnc.imported": "{added, plural, one {# number added or updated.} other {# numbers added or updated.}}",

  "developers.description": "Provider credentials, API keys and webhooks.",
  "developers.keys": "API keys",
  "developers.keysEmpty": "No API keys",
  "developers.keysEmptyBody": "Create one below; it is shown once.",
  "developers.keys.name": "New key's name",
  "developers.keys.create": "Create key",
  "developers.keys.createHint": "The key has your own permissions, no more. It is shown once, when it is made.",
  "developers.keys.madeTitle": "Key “{name}” created",
  "developers.keys.madeBody": "Copy it now and keep it somewhere safe: it is not shown again.",
  "developers.keys.theKey": "The new API key",
  "developers.keys.copy": "Copy",
  "developers.keys.copied": "Copied",
  "developers.keys.done": "Done",
  "developers.keys.actions": "Actions",
  "developers.keys.revoke": "Revoke",
  "developers.keys.revokeConfirm": "Revoke the key “{name}”? Anything using it stops working at once.",
  "developers.webhooks": "Webhooks",
  "developers.webhooksEmpty": "No webhook endpoints",
  "developers.webhooksEmptyBody": "Receive call events with POST /v1/webhook-endpoints.",
  "developers.col.name": "Name",
  "developers.col.prefix": "Key",
  "developers.col.lastUsed": "Last used",
  "developers.col.url": "URL",
  "developers.col.events": "Events",
  "developers.col.enabled": "Enabled",
  "developers.never": "Never",
  "developers.allEvents": "All",
  "credentials.title": "Provider credentials",
  "credentials.intro": "Keys for the speech, language and voice providers your agents use. An agent names a credential (for example spec.stt.credential: deepgram), so keep the name when you change the key. Keys are stored encrypted and are never shown again.",
  "credentials.empty": "No provider credentials yet",
  "credentials.emptyBody": "Add one below for each provider your agents use.",
  "credentials.col.name": "Name",
  "credentials.col.provider": "Provider",
  "credentials.col.fields": "Holds",
  "credentials.col.check": "Check",
  "credentials.col.updated": "Updated",
  "credentials.col.actions": "Actions",
  "credentials.status.ok": "Works",
  "credentials.status.failed": "Rejected by the provider",
  "credentials.status.unsupported": "No check for this provider yet",
  "credentials.status.unreachable": "Could not reach the provider",
  "credentials.checking": "Checking the key with the provider…",
  "credentials.status.none": "Not checked",
  "credentials.check": "Check",
  "credentials.checkWho": "Check {name}",
  "credentials.replace": "Replace key",
  "credentials.replaceWho": "Replace the key of {name}",
  "credentials.replaceHint": "Leave a field empty to keep what is stored. Agents that use {name} use the new key from their next call.",
  "credentials.replaceSave": "Save the new key",
  "credentials.replaced": "Saved. {name} now holds the new key.",
  "credentials.replaceNothing": "Fill in at least one field.",
  "credentials.delete": "Delete",
  "credentials.deleteWho": "Delete {name}",
  "credentials.deleteConfirm": "Delete {name}? Agents that name it cannot call until a credential with that name is added again.",
  "credentials.deleteYes": "Yes, delete",
  "credentials.add": "Add a credential",
  "credentials.provider": "Provider",
  "credentials.pickProvider": "Choose a provider",
  "credentials.name": "Name",
  "credentials.nameHint": "What agents call it. Use the name an agent already names to make it work.",
  "credentials.save": "Add credential",
  "credentials.added": "Added {name}.",
  "credentials.optional": "{label} (optional)",
  "credentials.field.api_key": "API key",
  "credentials.field.base_url": "Base URL",
  "credentials.field.region": "Region",
  "credentials.field.workspace_id": "Workspace ID",
  "credentials.field.service_account_json": "Service account JSON",
  "credentials.addFor": "Add a {provider} credential",

  "settings.description": "Members and roles, sign-in security, publishing, limits and, for resellers, your tenants.",
  "settings.members": "Members",
  "settings.publishing": "Publishing",
  "settings.evalsOnPublish": "When a version has not passed its eval suites",
  "settings.evals.off": "Publish anyway, without a word",
  "settings.evals.warn": "Warn in the publish dialog (the default)",
  "settings.evals.block": "Refuse to publish it",
  "settings.evalsHint": "Only suites that run on publish count, and only the version's latest run of each.",
  "settings.saved": "Saved.",
  "settings.membersEmpty": "No members",
  "settings.col.email": "Email",
  "settings.col.name": "Name",
  "settings.col.role": "Role",
  "settings.col.joined": "Joined",
  "settings.invited": "Invited",
  "settings.col.actions": "Actions",
  "settings.roleOf": "Role of {email}",
  "settings.you": "(you)",
  "settings.remove": "Remove",
  "settings.removeWho": "Remove {email}",
  "settings.removeConfirm": "Remove this member?",
  "settings.removeSelf": "Remove yourself? You lose access to this tenant.",
  "settings.removeYes": "Yes, remove",
  "settings.invite": "Invite someone",
  "settings.inviteSend": "Invite",
  "settings.inviteHint": "They join the first time they sign in with this email, verified. No email is sent: tell them.",
  "settings.invitedWho": "Invited {email}.",
  "settings.security": "Sign-in security",
  "settings.mfa": "Who signs in with a second factor (a one-time code)",
  "settings.mfa.admins": "Owners and admins (the default)",
  "settings.mfa.everyone": "Everyone",
  "settings.mfa.off": "No one",
  "settings.mfaHint": "Someone without a code set up is asked to set one up at their next sign-in. API keys are not affected.",
  "limits.title": "Limits and usage",
  "limits.unlimited": "Unlimited",
  "limits.ofLimit": "{used} of {limit}",
  "limits.usedNoLimit": "{used} (no limit)",
  "limits.concurrent": "Calls in progress",
  "limits.minutes": "Minutes since {since}",
  "limits.anyCountry": "Any country",
  "limits.none": "None",
  "limits.suspended": "This tenant is suspended: its calls are refused. Ask your provider to resume it.",
  "limits.hint": "A call over a limit is refused, and the call log and the usage.limit.reached event say which.",
  "analytics.export": "Export calls (CSV)",
  "reports.title": "Scheduled reports",
  "reports.intro": "Your numbers by email, with the calls attached as CSV: daily (yesterday's) or weekly (the seven days before).",
  "reports.col.name": "Name",
  "reports.col.when": "When",
  "reports.col.to": "To",
  "reports.col.last": "Last sent",
  "reports.dailyAt": "Daily at {hour}",
  "reports.weeklyAt": "{day}s at {hour}",
  "reports.off": "off",
  "reports.every": "Every",
  "reports.daily": "Day",
  "reports.weekly": "Week",
  "reports.dayLabel": "On",
  "reports.hour": "At (hour, your time zone)",
  "reports.emails": "To (email addresses)",
  "reports.add": "Schedule",
  "reports.new": "New scheduled report",
  "reports.send": "Send now",
  "reports.sendFor": "Send {name} now",
  "reports.sent": "Sent",
  "reports.notSent": "The mail server refused it",
  "reports.remove": "Remove",
  "reports.removeFor": "Remove {name}",
  "reports.day.0": "Monday",
  "reports.day.1": "Tuesday",
  "reports.day.2": "Wednesday",
  "reports.day.3": "Thursday",
  "reports.day.4": "Friday",
  "reports.day.5": "Saturday",
  "reports.day.6": "Sunday",
  "analytics.f.label": "Which calls",
  "analytics.f.any": "Any",
  "analytics.f.agent": "Agent",
  "analytics.f.direction": "Direction",
  "analytics.f.inbound": "Inbound",
  "analytics.f.outbound": "Outbound",
  "analytics.f.number": "Our number",
  "analytics.f.campaign": "Campaign",
  "analytics.description": "Your calls, their latency and their cost, by day in your time zone.",
  "analytics.period": "Period",
  "analytics.lastDays": "{n} days",
  "analytics.calls": "Calls",
  "analytics.minutes": "{minutes} billable minutes",
  "analytics.minutesHeader": "Minutes",
  "analytics.failed": "Failed",
  "analytics.failedHint": "{n, plural, one {# call} other {# calls}} (busy and unanswered are not failures)",
  "analytics.setup": "Call setup (p95)",
  "analytics.turn": "Response time (p95)",
  "analytics.p50": "median {value}",
  "analytics.seconds": "{s} s",
  "analytics.cost": "Estimated cost",
  "analytics.perCall": "{cost} a call",
  "analytics.callsADay": "Calls a day",
  "analytics.chart.label": "{calls} calls over {days} days, by outcome. The table below has the numbers.",
  "analytics.asTable": "Show as a table",
  "analytics.day": "Day",
  "analytics.outcome.completed": "Completed",
  "analytics.outcome.failed": "Failed",
  "analytics.outcome.busy": "Busy",
  "analytics.outcome.no_answer": "No answer",
  "analytics.outcome.blocked": "Blocked",
  "analytics.costByComponent": "Cost by component",
  "analytics.component": "Component",
  "analytics.failures": "Why calls failed",
  "analytics.noFailures": "No call failed in this period.",
  "analytics.reason": "Reason",
  "analytics.sip": "SIP code",
  "analytics.fallbacks": "Provider fallbacks",
  "analytics.noFallbacks": "No provider fell back in this period.",
  "analytics.kind": "Kind",
  "analytics.fromTo": "From → to",
  "analytics.times": "Times",
  "analytics.tools": "Tools",
  "analytics.noTools": "No tool was called in this period.",
  "analytics.tool": "Tool",
  "analytics.errors": "Errors",
  "analytics.empty.title": "No calls in this period",
  "analytics.empty.body": "Make a call, or pick a longer period. Analytics shows calls once they have ended.",
  "limits.field.max_concurrent_calls": "Concurrent calls",
  "limits.field.max_cps": "New calls per second",
  "limits.field.max_monthly_minutes": "Minutes a month",
  "limits.field.recording_retention_days": "Days recordings are kept",
  "limits.field.allowed_countries": "Countries outbound calls may reach",
  "limits.field.blocked_prefixes": "Blocked number prefixes",
  "limits.countriesHint": "Two-letter codes, separated by commas. Empty: any country.",
  "limits.childHint": "Empty means unlimited, or your own limit if you have one: a tenant cannot exceed its reseller.",
  "limits.save": "Save limits",
  "limits.short.concurrent": "{n} calls at once",
  "limits.short.minutes": "{n} min/month",
  "tenants.title": "Your tenants",
  "tenants.empty": "No tenants yet",
  "tenants.emptyBody": "Make one below. Then invite its people and make its keys while acting in it.",
  "tenants.col.name": "Tenant",
  "tenants.col.kind": "Kind",
  "tenants.col.status": "Status",
  "tenants.col.limits": "Limits",
  "tenants.col.actions": "Actions",
  "tenants.kind.standard": "Standard",
  "tenants.kind.reseller": "Reseller",
  "tenants.active": "Active",
  "tenants.suspended": "Suspended",
  "tenants.limits": "Limits",
  "tenants.limitsOf": "Limits of {name}",
  "tenants.limitsIntro": "These replace all of the tenant's limits.",
  "tenants.suspend": "Suspend",
  "tenants.resume": "Resume",
  "tenants.suspendWho": "Suspend {name}",
  "tenants.resumeWho": "Resume {name}",
  "tenants.new": "New tenant",
  "tenants.newHint": "It starts with your limits. Its country sets its compliance rules.",
  "tenants.field.name": "Name",
  "tenants.field.slug": "Slug",
  "tenants.field.country": "Country (ISO code)",
  "tenants.field.timezone": "Time zone",
  "tenants.slugRule": "2 to 63 characters: lowercase letters, digits and -, starting with a letter or digit.",
  "tenants.create": "Create tenant",
  "tenants.created": "Created {name}.",

  "format.seconds": "{s} s",
  "format.minutes": "{m} min {s} s",

  "common.close": "Close",
  "common.cancel": "Cancel",
  "models.description": "Your own STT, LLM, TTS, embedding and classifier models. An agent, a collection or a Call Script uses one by its name.",
  "models.new": "Register a model",
  "models.empty": "No models of your own yet",
  "models.emptyBody": "Register a model your agents can use: a server on your GPUs, or any API that speaks OpenAI's.",
  "models.endpoints": "Model endpoints",
  "models.metrics": "Provider latency, last 15 minutes",
  "models.metricsBody": "Measured on calls: each provider's time to its first byte (STT to the transcript, an LLM to its first token, TTS to its first audio), and how often a call fell back from it. Routing by latency leads with the fastest.",
  "models.metricsEmpty": "No calls in the last 15 minutes to measure the providers by.",
  "models.metrics.role": "Role",
  "models.metrics.provider": "Provider",
  "models.metrics.model": "Model",
  "models.metrics.turns": "Turns",
  "models.metrics.p50": "Median",
  "models.metrics.p95": "95th percentile",
  "models.metrics.failures": "Fell back",
  "models.metrics.failed": "{failures} of {attempts}",
  "models.metrics.unmeasured": "Not enough turns yet",
  "models.ms": "{ms} ms",
  "models.name": "Name",
  "models.nameHint": "Lowercase letters, digits and dashes: how agents name it.",
  "models.kind": "Kind",
  "models.kind.stt": "Speech to text",
  "models.kind.llm": "Language model",
  "models.kind.tts": "Text to speech",
  "models.kind.embedding": "Embedding",
  "models.kind.classifier": "Classifier",
  "models.protocol": "Protocol",
  "models.protocol.openai": "OpenAI-compatible",
  "models.protocol.ws": "swaralo-ws (streaming)",
  "models.protocolHint": "Streaming speech can use the swaralo-ws protocol; everything else speaks OpenAI's API.",
  "models.url": "URL",
  "models.urlHint": "The API's base, e.g. https://gpu.example.com/v1. A private address must be allowed by the operator.",
  "models.urlHintWs": "The WebSocket's address, e.g. wss://gpu.example.com/asr.",
  "models.model": "Default model",
  "models.credential": "Credential",
  "models.noCredential": "None",
  "models.region": "Region",
  "models.regionHint": "Where it processes data (in, us, eu…): the residency check uses it.",
  "models.price.perMinute": "USD per minute of audio",
  "models.price.input": "USD per million input tokens",
  "models.price.output": "USD per million output tokens",
  "models.price.characters": "USD per million characters",
  "models.register": "Register",
  "models.cancel": "Cancel",
  "models.lastCheck": "Last check",
  "models.notChecked": "Not checked yet",
  "models.passed": "Passed",
  "models.failed": "Failed",
  "models.took": "{ms} ms",
  "models.actions": "Actions",
  "models.check": "Check",
  "models.checkOne": "Check {name}",
  "models.checking": "Checking…",
  "models.remove": "Remove {name}",
  "models.removeConfirm": "Remove the model endpoint {name}? Agents that use it cannot be published until they use another.",

  "scripts.classifier": "Intents",
  "scripts.classifierHint": "What turns a caller's words into one of a gather's intents. Your hooks list the intents: {\"verb\": \"gather\", \"input\": [\"speech\"], \"intents\": [{\"name\": \"billing\"}]}; the action hook gets intent and confidence.",
  "scripts.classifierProvider": "Classifier",
  "scripts.classifierNone": "None (a gather with intents fails)",
  "scripts.classifier.openai": "OpenAI",
  "scripts.classifier.groq": "Groq",
  "scripts.classifier.openai-compatible": "OpenAI-compatible server",
  "scripts.classifier.endpoint": "One of your model endpoints",
  "scripts.classifier.fake": "Test classifier (offline, word matching)",

  "chat.title": "Chat",
  "chat.description": "Ask the copilot about your setup and your calls, and build by describing what you want. It drafts; you apply.",
  "chat.new": "New chat",
  "chat.list": "Your chats",
  "chat.none": "No chats yet.",
  "chat.untitled": "A chat",
  "chat.conversation": "The conversation",
  "chat.messages": "Messages",
  "chat.you": "You",
  "chat.copilot": "The copilot",
  "chat.hello": "What would you like to build or know?",
  "chat.helloNote": "The copilot looks things up in your account as you, with your permissions, and drafts what you ask for. Nothing changes until you apply a draft.",
  "chat.suggestions": "Suggestions",
  "chat.suggest.agent": "Build an agent that books appointments for a dental clinic",
  "chat.suggest.calls": "Which of my agents had the most failed calls this week?",
  "chat.suggest.campaign": "Set up a campaign that reminds patients of their appointments",
  "chat.suggest.evals": "Write an eval suite for my support agent",
  "chat.loading": "Loading the chat…",
  "chat.thinking": "The copilot is thinking…",
  "chat.delete": "Delete this chat",
  "chat.usage": "{tokens, number} tokens so far · {model}",
  "chat.questions": "The copilot asks",
  "chat.lookedUp": "{count, plural, one {Looked up # thing} other {Looked up # things}}{refused, plural, =0 {} one {, # refused} other {, # refused}}",
  "chat.refused": "refused",
  "chat.cannotTalk": "Talking to the copilot needs permission to write agents (it uses your organization's copilot model).",
  "chat.message": "Message the copilot",
  "chat.placeholder": "Describe what you want, or ask about your calls…",
  "chat.hint": "Enter sends; Shift+Enter starts a new line.",
  "chat.send": "Send",
  "chat.kind.agent": "New agent",
  "chat.kind.change": "Agent change",
  "chat.kind.script": "Call Script app",
  "chat.kind.campaign": "Campaign",
  "chat.kind.assist": "Assist profile",
  "chat.kind.suite": "Eval suite",
  "chat.ready": "Ready to apply",
  "chat.invalid": "Not valid",
  "chat.applied": "Applied",
  "chat.discarded": "Discarded",
  "chat.problems": "What is wrong with it",
  "chat.notices": "Checks",
  "chat.apply": "Apply",
  "chat.discard": "Discard",
  "chat.applyNote": "Applying creates it as you. An agent's new version is a draft until you publish it.",
  "chat.campaignNote": "Applying creates the campaign as a draft. You add its contacts and start it on its page.",
  "chat.secret": "The app's signing secret, shown this once. Keep it now:",
  "chat.changes": "The changes",
  "chat.name": "Name",
  "chat.document": "The whole document",
  "chat.openAgent": "Open the agent",
  "chat.openVersion": "Open the agent (v{version} is its new draft)",
  "chat.openSuite": "Open the agent's eval suites",
  "chat.openCampaign": "Open the campaign",
  "chat.openScript": "Open the Call Script app",
  "chat.openAssist": "Open Agent assist",
  "common.yes": "Yes",
  "common.no": "No",
} as const;

export type MessageId = keyof typeof en;
